import * as PropTypes from 'prop-types';

import {CoreObjectArchiveData} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {ArchiveRecord, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {AssaysFeature, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {BasicPermissionsGraphQL, Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {StringIDFolder} from 'constants/FolderConstants';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RegistryFilters, TagSchema} from 'constants/RegistryConstants';
import {BatchSchemaFilters, StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary, Tag} from 'folder-items/FileConstants';
import {
  ProcessClientOrigin,
  ProcessExecutionType,
  ReviewerStatus,
  ReviewLifecycleTerminalState,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {MissingMonomerReason} from 'molbio/monomers/MonomerConstants';
import {SearchView} from 'nav/sidepanel/listing/search-results/SearchResultsView';
import {EntrySummary, ProtocolSummary} from 'notebook/ELNConstants';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {SidepanelProcessItem} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {getLegacyRequestObjectName, Request} from 'requests/RequestConstants';
import {SidepanelRun} from 'runs/sidepanel/RunsSidepanelConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import APIUtil from 'util/APIUtil';
import {typeToPluralized} from 'util/NameUtil';
import {assertNever, JSONValue, Optional} from 'util/Types';

import {AnalysisSearchAttributesGraphQL} from '../../graphql-fragments/analysis-tool/__generated/AnalysisSearchAttributesGraphQL';
import {JellyfishListingGraphQL} from '../../graphql-fragments/app-core/archetype/__generated/JellyfishListingGraphQL';
import {ReactionGraphQL} from '../../graphql-fragments/chemistry/__generated/ReactionGraphQL';
import {DatasetListingGraphQL} from '../../graphql-fragments/data-frames/__generated/DatasetListingGraphQL';
import {FileListingGraphQL} from '../../graphql-fragments/files/__generated/FileListingGraphQL';
import {AnalyticsDashboardSearchAttributesGraphQL} from '../../graphql-fragments/insights/__generated/AnalyticsDashboardSearchAttributesGraphQL';
import {ConnectionListingGraphQL} from '../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {BulkAssemblyListingGraphQL} from '../../graphql-fragments/molbio/__generated/BulkAssemblyListingGraphQL';
import {ProteinAlignmentSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/ProteinAlignmentSummaryGraphQL';
import {SequenceAnalysisSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/SequenceAnalysisSummaryGraphQL';
import {CollectibleListingGraphQL} from '../../graphql-fragments/notebook/templates/__generated/CollectibleListingGraphQL';
import {TemplateCollectionListingGraphQL} from '../../graphql-fragments/notebook/templates/__generated/TemplateCollectionListingGraphQL';
import {ProcessListingGraphQL} from '../../graphql-fragments/processes/__generated/ProcessListingGraphQL';
import {ProcessOutputListingGraphQL} from '../../graphql-fragments/processes/__generated/ProcessOutputListingGraphQL';
import {ProcessTaskListingGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {RequestV2DefinitionListingGraphQL} from '../../graphql-fragments/processes/requests-v2/__generated/RequestV2DefinitionListingGraphQL';
import {RequestV2SubmissionListingGraphQL} from '../../graphql-fragments/processes/requests-v2/__generated/RequestV2SubmissionListingGraphQL';
import {TestDefinitionListingGraphQL} from '../../graphql-fragments/sample_tests/__generated/TestDefinitionListingGraphQL';
import {TestOrderListingGraphQL} from '../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';
import {StudyListingGraphQL} from '../../graphql-fragments/studies/__generated/StudyListingGraphQL';
import {StageEntrySummaryGraphQL} from '../../graphql-fragments/workflows/__generated/StageEntrySummaryGraphQL';
import {WorkflowSummaryGraphQL} from '../../graphql-fragments/workflows/__generated/WorkflowSummaryGraphQL';
import {WorklistSummaryGraphQL} from '../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {WorksheetListingGraphQL} from '../../graphql-fragments/worksheets/__generated/WorksheetListingGraphQL';

export type BaseItem =
  | AnalysisSearchAttributesGraphQL
  | AnalyticsDashboardSearchAttributesGraphQL
  | BioentitySummary
  | BulkAssemblyListingGraphQL
  | CollectibleListingGraphQL
  | ConnectionListingGraphQL
  | DatasetListingGraphQL
  | EntrySummary
  | FileListingGraphQL
  | JellyfishListingGraphQL
  | ProcessListingGraphQL
  | ProcessTaskListingGraphQL
  | ProteinAlignmentSummaryGraphQL
  | ProtocolSummary
  | ReactionGraphQL
  | Request
  | RequestV2DefinitionListingGraphQL
  | RequestV2SubmissionListingGraphQL
  | SequenceAnalysisSummaryGraphQL
  | SidepanelProcessItem
  | SidepanelRun
  | StageEntrySummaryGraphQL
  | StorableSummary
  | StringIDFolder
  | StudyListingGraphQL
  | TemplateCollectionListingGraphQL
  | TestDefinitionListingGraphQL
  | TestOrderListingGraphQL
  | WorkflowSummaryGraphQL
  | WorklistSummaryGraphQL
  | WorksheetListingGraphQL;

export interface ListableItemExtras {
  // Added explicitly by listing code (e.g. ItemListing, BioentityListing).
  permissions: Permissions | BasicPermissionsGraphQL;
  // Added by ItemListing but not others.
  tags?: Array<Optional<Tag>>;
  // Some fields only appear on some types but we use existence to determine if we should use them.
  archiveRecord?: ArchiveRecord | null;
  archiveData?: CoreObjectArchiveData | null;
  schemaValidationState?: SchemaValidationState | null;
  tagSchema?: TagSchema | null;
  worklistItemPosition?: number;
  worklistId?: string | null;
  // Additional attributes that come from the search system. These are specific to the search request,
  // as opposed to other model properties which are the same regardless of context. Added by listing code.
  attributeValues?: {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  // TODO(BNCH-145194): remove this type after reaction search prototype.
  reactionContext?: ReactionContext;
}

export type ListableItem = BaseItem & ListableItemExtras;

/**
 * A group that can be expanded to reveal a set of listable items.
 */
export interface ListableGroup {
  /** A unique id for rendering purposes. */
  id: string;
  /** A non-unique label for the group. */
  label: string;
  /** Total number of items in the group, fetched or not. */
  count: number;
  /** The items that have been fetched. There may be fewer than `count`. */
  items: Array<ListableItem>;
  /** The aggregate field and value pairs for this group. */
  aggregates: GroupAggregates;
  /** The group is expanded. */
  isExpanded: boolean;
  /** The item (if a single entity link value) that the group header points to. */
  groupItem?: ListableItem;
  /** If there are new children being fetched that aren't yet loaded. */
  areChildrenLoading: boolean;
}

export const ALL_LISTABLE_TYPES = [
  ModelTypeConstants.ANALYSIS,
  ModelTypeConstants.ANALYSIS_TEMPLATE,
  ModelTypeConstants.ANALYTICS_DASHBOARD,
  ModelTypeConstants.ANTIBODY,
  ModelTypeConstants.EQUIPMENT,
  ModelTypeConstants.OLIGO_CONJUGATE,
  ModelTypeConstants.OLIGO_DUPLEX,
  ModelTypeConstants.FOLDER,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.RNA_SEQUENCE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.DNA_OLIGO,
  ModelTypeConstants.RNA_OLIGO,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.PROTOCOL,
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.PLATE,
  ModelTypeConstants.BOX,
  ModelTypeConstants.LOCATION,
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.PROCESS_TASK,
  ModelTypeConstants.WORKFLOW,
  ModelTypeConstants.STAGE_ENTRY,
  ModelTypeConstants.REQUEST,
  ModelTypeConstants.REQUEST_V2_SUBMISSION,
  ModelTypeConstants.REQUEST_V2_DEFINITION,
  ModelTypeConstants.SEQUENCE_ANALYSIS,
  ModelTypeConstants.PROTEIN_ALIGNMENT,
  ModelTypeConstants.CONNECTION,
  ModelTypeConstants.ASSAY_RUN,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.WORKLIST,
  ...(BenchlingConfig.APP_CORE_ENABLE_FAKE_ITEM_TYPE ? [ModelTypeConstants.JELLYFISH] : []),
  ...(BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS ? [ModelTypeConstants.DATASET] : []),
  ...(BenchlingConfig.STUDIES_ENABLE ? [ModelTypeConstants.STUDY] : []),
  ...(isFeatureEnabled({feature: RecipesFeature.RECIPES_DISCOVER})
    ? [
        ModelTypeConstants.RECIPE_PROCEDURE,
        ModelTypeConstants.RECIPE_RUN_TASK,
        ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
        ModelTypeConstants.RECIPE_STEP_TASK,
      ]
    : []),
  ...(isFeatureEnabled({feature: AssaysFeature.ASSAYS_DISCOVER})
    ? [
        ModelTypeConstants.ASSAY_PROCEDURE,
        ModelTypeConstants.ASSAY_RUN_TASK,
        ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
        ModelTypeConstants.ASSAY_STEP_TASK,
      ]
    : []),
  ...(areAnyFeaturesEnabled([
    {feature: RecipesFeature.RECIPES_DISCOVER},
    {feature: AssaysFeature.ASSAYS_DISCOVER},
  ])
    ? [ModelTypeConstants.WORKSHEET]
    : []),
  ModelTypeConstants.TEMPLATE,
  ModelTypeConstants.SUBTEMPLATE,
  ModelTypeConstants.FORM_DEFINITION,
  ModelTypeConstants.TEMPLATE_COLLECTION,
  ...(BenchlingConfig.SAMPLE_TESTS_ENABLE
    ? [ModelTypeConstants.TEST_DEFINITION, ModelTypeConstants.TEST_ORDER]
    : []),
  ModelTypeConstants.FILE,
];

export function isProcessTaskOrOutput(
  item: ListableItem
): item is (ProcessTaskListingGraphQL | ProcessOutputListingGraphQL) & ListableItemExtras {
  return APIUtil.isProcessTask(item.id) || APIUtil.isProcessOutput(item.id);
}

export function isStudy(item: ListableItem): item is StudyListingGraphQL & ListableItemExtras {
  return APIUtil.isStudy(item.id);
}

export function isTestOrder(item: ListableItem): item is TestOrderListingGraphQL & ListableItemExtras {
  return APIUtil.isTestOrder(item.id);
}

export function isSidepanelProcessItem(
  item: ListableItem
): item is SidepanelProcessItem & ListableItemExtras {
  return APIUtil.isProcessTask(item.id) || APIUtil.isProcessOutput(item.id) || APIUtil.isProcess(item.id);
}

export function isStorableSummary(item: ListableItem): item is StorableSummary & ListableItemExtras {
  return APIUtil.isStorable(item.id);
}

export const SortPreferences = {
  SORTS_NAMESPACE: 'SORTS_',
};

export const SEARCH_FILTER_PREFERENCE = 'SEARCH_FILTER_';

export const SAVED_SEARCH_PREFERENCE = 'SAVED_SEARCH_';

// *** PARTIALLY GENERATED CODE ***
//  This enum will be modified by code generation tools. It is okay to add new keys
//  to the enum, but if the enum signature changes, the regexes in
//  scripts/search/code_generation/generate_group_by_key.py will need to be updated.
export enum GroupByKey {
  NONE = '',
  CONTAINER_CONTENTS = 'container_contents',
  BLAST_QUERY = 'blast_query',
  // The RECIPE_* keys do not match the value to maintain backwards compatibility
  RECIPE_METHOD_TASK = 'procedure_method',
  RECIPE_RUN_TASK = 'procedure_run',
  ASSAY_RUN_TASK = 'assay_run_task',
  ASSAY_METHOD_TASK = 'assay_method_task',
  CONTAINER = 'container',
  SAMPLE = 'sample',
  PROCESS_TASK_GROUP = 'process_task_group',
}

export enum FilterKey {
  // These comments signify the main block that should be maintained in sorted order and that new filter keys
  // should be added to by code generation. It is perfectly okay to modify it manually as long as it is
  // fully enclosed in these comments and the items can be rearranged freely. This primarily exists to
  // allow filtrs to be grouped outside of the main sort order, such as the query API filters below that
  // are commented as a unit.

  // --TARGET FOR CODE GENERATION--
  AA_PART_SEQUENCE = 'AA_PARTS',
  ACCOUNT_STATUS = 'ACCOUNT_STATUS',
  ALPHAFOLD_STRUCTURE = 'ALPHAFOLD_STRUCTURE',
  ANCESTOR_FOLDER = 'ANCESTOR_FOLDER',
  ANCESTOR_STORABLE = 'ANCESTOR_STORABLE',
  ANNOTATION = 'ANNOTATION',
  ANY_LINK_SCHEMA_FIELD = 'ANY_LINK_SCHEMA_FIELD',
  API_IDS = 'API_IDS',
  ARCHIVE_PURPOSES = 'ARCHIVE_PURPOSES',
  ARCHIVE_STATUS = 'ARCHIVE_STATUS',
  ASSAY_CONDITION_REPLICATE_RUN_TASKS = 'ASSAY_CONDITION_REPLICATE_RUN_TASKS',
  ASSAY_METHOD_NAME = 'ASSAY_METHOD_NAME',
  ASSAY_RUN_CONDITION_NAMES = 'ASSAY_RUN_CONDITION_NAMES',
  ASSAY_RUN_REPLICATE_NAMES = 'ASSAY_RUN_REPLICATE_NAMES',
  ASSAY_SCHEMA = 'ASSAY_SCHEMA',
  ASSAY_TEMPLATE = 'ASSAY_TEMPLATE',
  AUTHOR = 'AUTHOR',
  BARCODES = 'BARCODES',
  BATCH_TAG = 'BATCH_TAG',
  BIOENTITY_LABELS = 'BIOENTITY_LABELS',
  BLAST = 'BLAST',
  CAPABILITIES = 'CAPABILITIES',
  CHEMICAL_STRUCTURE = 'CHEMICAL_STRUCTURE',
  CONNECTION = 'CONNECTION',
  CONNECTION_ADAPTER = 'CONNECTION_ADAPTER',
  CONNECTION_GATEWAY = 'CONNECTION_GATEWAY',
  CONTAINER_CHECK_OUT_ASSIGNEE = 'CONTAINER_CHECK_OUT_ASSIGNEE',
  CONTAINER_CHECK_OUT_STATUS = 'CONTAINER_CHECK_OUT_STATUS',
  CONTAINER_CONCENTRATION = 'CONTAINER_CONCENTRATION',
  CONTAINER_CONTENTS = 'CONTAINER_CONTENTS',
  CONTAINER_CONTENT_SCHEMA = 'CONTAINER_CONTENT_SCHEMA',
  CONTAINER_PARENT = 'CONTAINER_PARENT',
  CONTAINER_QUANTITY = 'CONTAINER_VOLUME',
  CREATED_AT = 'CREATED_AT',
  CREATION_ORIGIN_ENTRY = 'CREATION_ORIGIN_ENTRY',
  CREATOR = 'CREATOR',
  CUSTOM_TAG = 'CUSTOM_TAG',
  DILUTION_FACTOR = 'DILUTION_FACTOR',
  ENTRY_TEMPLATE = 'ENTRY_TEMPLATE',
  EXPIRATION_DATE = 'EXPIRATION_DATE',
  FOLDER = 'FOLDER',
  FOLDER_SECTION = 'FOLDER_SECTION',
  FREEZE_THAW_COUNT = 'FREEZE_THAW_COUNT',
  GRID_EMPTY_CONTAINERS = 'GRID_EMPTY_CONTAINERS',
  GRID_EMPTY_POSITIONS = 'GRID_EMPTY_POSITIONS',
  GROUP_BY_KEY = 'GROUP_BY_KEY',
  HIDDEN = 'HIDDEN',
  INGREDIENTS = 'INGREDIENTS',
  INSTITUTION = 'INSTITUTION',
  INVENTORY_VALIDATION_ERROR_LABEL = 'INVENTORY_VALIDATION_ERROR_LABEL',
  INVENTORY_VALIDATION_STATUS = 'INVENTORY_VALIDATION_STATUS',
  IN_STUDY = 'IN_STUDY',
  IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION = 'IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION',
  IS_EXPIRED = 'IS_EXPIRED',
  IS_FILL_IN_ENTRY = 'IS_FILL_IN_ENTRY',
  ITEM_IN_WORKLIST = 'ITEM_IN_WORKLIST',
  LINEAGE_PARENT_API_IDENTIFIER = 'LINEAGE_PARENT_API_IDENTIFIER',
  MODIFIED_AT = 'MODIFIED_AT',
  MONOMER = 'MONOMER',
  NAMES = 'NAMES',
  NOTE_REFERENCE = 'NOTE_REFERENCE',
  OLIGO_HELM_DNA = 'OLIGO_HELM_DNA',
  OLIGO_HELM_RNA = 'OLIGO_HELM_RNA',
  ORGANIZATION = 'ORGANIZATION',
  OWNER = 'OWNER',
  OWNER_OR_COLLABORATOR = 'OWNER_OR_COLLABORATOR',
  PARENT_STORABLE = 'PARENT_STORABLE',
  PART_SEQUENCE = 'PARTS',
  POLICY = 'POLICY',
  PROCEDURE_TASK_STATUS = 'PROCEDURE_TASK_STATUS',
  PROCESS = 'PROCESS',
  PROCESSES_IS_SYSTEM_DATA_FILTER = 'PROCESSES_IS_SYSTEM_DATA_FILTER',
  PROCESS_CLIENT_ORIGIN = 'PROCESS_CLIENT_ORIGIN',
  PROCESS_EXECUTION_TYPE = 'PROCESS_EXECUTION_TYPE',
  // responsibleTeam is no longer used, but keeping the constant here for backwards compatibility
  // we now use responsibleParties instead
  PROCESS_RESPONSIBLE_TEAM = 'PROCESS_RESPONSIBLE_TEAM',
  PROCESS_STATUS = 'PROCESS_STATUS',
  PROCESS_TASK = 'PROCESS_TASK',
  PROCESS_TASK_ASSIGNEE = 'PROCESS_TASK_ASSIGNEE',
  PROCESS_TASK_EXECUTION_ORIGIN_ENTRY = 'PROCESS_TASK_EXECUTION_ORIGIN_ENTRY',
  PROCESS_TASK_HAS_FAILED_MAPPINGS = 'PROCESS_TASK_HAS_FAILED_MAPPINGS',
  PROCESS_TASK_RESPONSIBLE_PARTIES = 'PROCESS_TASK_RESPONSIBLE_PARTIES',
  PROCESS_TASK_SCHEDULED_ON_DATE = 'PROCESS_TASK_SCHEDULED_ON_DATE',
  PROCESS_TASK_STATUS = 'PROCESS_TASK_STATUS',
  PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES = 'PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES',
  PROCESS_WATCHER = 'PROCESS_WATCHER',
  PROJECT = 'PROJECT',
  PROJECT_ATTRIBUTE = 'PROJECT_ATTRIBUTE',
  PROJECT_FOLDER = 'PROJECT_FOLDER',
  PROTEIN_AMINO_ACIDS = 'PROTEIN_AMINO_ACIDS',
  PROTEIN_PROSITE = 'PROTEIN_PROSITE',
  PUBLISHING_STATUS = 'PUBLISHING_STATUS',
  RECIPE_CONDITION_REPLICATE_RUN_TASKS = 'RECIPE_CONDITION_REPLICATE_RUN_TASKS',
  RECIPE_METHOD_NAME = 'RECIPE_METHOD_NAME',
  RECIPE_RUN_CONDITION_NAMES = 'RECIPE_RUN_CONDITION_NAMES',
  RECIPE_RUN_REPLICATE_NAMES = 'RECIPE_RUN_REPLICATE_NAMES',
  RECIPE_TEMPLATE = 'RECIPE_TEMPLATE',
  REGISTRATION_STATUS = 'REGISTRATION_STATUS',
  REGISTRY = 'REGISTRY',
  REGISTRY_COLLECTION_OWNER = 'REGISTRY_COLLECTION_OWNER',
  REQUEST_ASSIGNEE = 'REQUEST_ASSIGNEE',
  REQUEST_IDS = 'REQUEST_IDS',
  REQUEST_REQUESTOR = 'REQUEST_REQUESTOR',
  REQUEST_STATUS = 'REQUEST_STATUS',
  REQUEST_TASK_SCHEMA = 'REQUEST_TASK_SCHEMA',
  REQUEST_V2_DEFINITION = 'REQUEST_V2_DEFINITION',
  REQUEST_V2_SUBMISSION = 'REQUEST_V2_SUBMISSION',
  REQUEST_V2_SUBMISSION_STATUS = 'REQUEST_V2_SUBMISSION_STATUS',
  REVIEW = 'REVIEW',
  REVIEW_COMPLETED = 'REVIEW_COMPLETED',
  REVIEW_INITIAL_STARTED = 'REVIEW_INITIAL_STARTED',
  REVIEW_LIFECYCLE = 'REVIEW_LIFECYCLE',
  REVIEW_LIFECYCLE_TYPE = 'REVIEW_LIFECYCLE_TYPE',
  REVIEW_STARTED = 'REVIEW_STARTED',
  RNA_PART_SEQUENCE = 'RNA_PARTS',
  ROOT_PROCESS = 'ROOT_PROCESS',
  ROOT_PROCESS_CREATOR = 'ROOT_PROCESS_CREATOR',
  ROOT_PROCESS_TASK_SCHEMA = 'ROOT_PROCESS_TASK_SCHEMA',
  ROOT_PROCESS_WATCHER = 'ROOT_PROCESS_WATCHER',
  RUN_AVAILABILITY = 'RUN_AVAILABILITY',
  SAMPLE = 'SAMPLE',
  SAMPLE_AUTHORIZED_USERS = 'SAMPLE_AUTHORIZED_USERS',
  SAMPLE_CONTAINER = 'SAMPLE_CONTAINER',
  SAMPLE_DEFINITION = 'SAMPLE_DEFINITION',
  SAMPLE_OWNERS = 'SAMPLE_OWNERS',
  SAMPLE_RESTRICTION_ACCESS = 'SAMPLE_RESTRICTION_ACCESS',
  SAMPLE_RESTRICTION_STATUS = 'SAMPLE_RESTRICTION_STATUS',
  SCHEMA = 'SCHEMA',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  SCHEMA_INTERFACE = 'SCHEMA_INTERFACE',
  SCHEMA_VALIDATION_ERROR_LABEL = 'SCHEMA_VALIDATION_ERROR_LABEL',
  SCHEMA_VALIDATION_STATUS = 'SCHEMA_VALIDATION_STATUS',
  SEQUENCE_BASES = 'SEQUENCE_BASES',
  SESSION_NAME = 'SESSION_NAME',
  SESSION_STATUS = 'SESSION_STATUS',
  SESSION_MODIFIED_AT = 'SESSION_MODIFIED_AT',
  SHARED_WITH = 'SHARED_WITH',
  STARRED = 'STARRED',
  STUDY_PHASE = 'STUDY_PHASE',
  TAG = 'TAG',
  TEAM = 'TEAM',
  TEMPLATE_COLLECTION = 'TEMPLATE_COLLECTION',
  TEST_STATUS = 'TEST_STATUS',
  TOP_LEVEL_PERMISSIONS = 'TOP_LEVEL_PERMISSIONS',
  TRANSCRIPTIONS = 'TRANSCRIPTIONS',
  TYPE = 'TYPE',
  TYPES = 'TYPES',
  WORKFLOW_STATUS = 'WORKFLOW_STATUS',
  WORKFLOW_TEMPLATE = 'WORKFLOW_TEMPLATE',
  WORKLIST_TYPE = 'WORKLIST_TYPE',
  WORKSHEET = 'WORKSHEET',
  // --END TARGET FOR CODE GENERATION--

  // FilterKeys for Query API; do not use without discussing with Assays team.
  // These keys are not used in the regular search backend, but are rather interpolated into a GraphQL
  // query that is run against the Query API. Therefore, they are not handled on the search backend and are
  // separated out from corresponding filter keys above.
  QUERY_API_BIOENTITY_ID = 'QUERY_API_BIOENTITY_ID',
  QUERY_API_ARCHIVE_REASON = 'QUERY_API_ARCHIVE_REASON',
  QUERY_API_MODIFIED_AT = 'QUERY_API_MODIFIED_AT',
  QUERY_API_CREATED_AT = 'QUERY_API_CREATED_AT',
  QUERY_API_AUTHOR = 'QUERY_API_AUTHOR',
  QUERY_API_CREATOR = 'QUERY_API_CREATOR',
  QUERY_API_PROJECT = 'QUERY_API_PROJECT',
  QUERY_API_REGISTRY = 'QUERY_API_REGISTRY',
}

export enum Operator {
  BETWEEN = 'BETWEEN',
  BLASTN = 'BLASTN',
  BLASTP = 'BLASTP',
  BLASTX = 'BLASTX',
  TBLASTN = 'TBLASTN',
  TBLASTX = 'TBLASTX',
  CONTAINS = 'CONTAINS',
  EQ = 'EQ',
  EXCLUDES_TEXT = 'EXCLUDES_TEXT',
  GTE = 'GTE',
  HAS_ALL_OF = 'HAS_ALL_OF',
  HAS_NONE_OF = 'HAS_NONE_OF',
  HAS_ONE_OF = 'HAS_ONE_OF',
  HAS_ONLY = 'HAS_ONLY',
  INCLUDES_TEXT = 'INCLUDES_TEXT',
  IS = 'IS',
  IS_EMPTY = 'IS_EMPTY',
  IS_NONE_OF = 'IS_NONE_OF',
  IS_NOT = 'IS_NOT',
  IS_NOT_EMPTY = 'IS_NOT_EMPTY',
  IS_ONE_OF = 'IS_ONE_OF',
  IS_TRUE = 'IS_TRUE',
  IS_FALSE = 'IS_FALSE',
  LTE = 'LTE',
  MATCHES = 'MATCHES',
  SIMILARITY = 'SIMILARITY',
  STARTSWITH_TEXT = 'STARTSWITH_TEXT',
  SUBSTRUCTURE = 'SUBSTRUCTURE',
  CURRENT_USER = 'CURRENT_USER',
  RELATIVE_RANGE = 'RELATIVE_RANGE',
  // These are client side only operators that serve as shortcuts for various
  // relative date ranges. They only exist in this enum to make them valid
  // values for OperatorDropdown and should be mapped to RELATIVE_RANGE with
  // and appropriate value before being passed to the search API.
  LAST_WEEK = 'LAST_WEEK',
  NEXT_WEEK = 'NEXT_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  NEXT_MONTH = 'NEXT_MONTH',
}

export const RELATIVE_RANGE_SHORTCUT_OPERATORS = [
  Operator.NEXT_WEEK,
  Operator.LAST_WEEK,
  Operator.NEXT_MONTH,
  Operator.LAST_MONTH,
];

export const OPERATOR_TO_TEXT = {
  [Operator.BETWEEN]: 'between',
  [Operator.BLASTN]: 'Nucleotide BLAST (blastn)',
  [Operator.BLASTP]: 'Protein BLAST (blastp)',
  [Operator.BLASTX]: 'blastx',
  [Operator.TBLASTN]: 'tblastn',
  [Operator.TBLASTX]: 'tblastx',
  [Operator.CONTAINS]: 'contains',
  [Operator.EQ]: 'equals',
  [Operator.EXCLUDES_TEXT]: 'excludes text',
  [Operator.GTE]: 'is at least',
  [Operator.HAS_NONE_OF]: 'has none of',
  [Operator.HAS_ONE_OF]: 'has one of',
  [Operator.HAS_ALL_OF]: 'has all of',
  [Operator.HAS_ONLY]: 'has only',
  [Operator.INCLUDES_TEXT]: 'includes text',
  [Operator.IS]: 'is',
  [Operator.IS_EMPTY]: 'is empty',
  [Operator.IS_NONE_OF]: 'is none of',
  [Operator.IS_NOT]: 'is not',
  [Operator.IS_NOT_EMPTY]: 'is not empty',
  [Operator.IS_ONE_OF]: 'is one of',
  [Operator.IS_TRUE]: 'is true',
  [Operator.IS_FALSE]: 'is false',
  [Operator.LTE]: 'is at most',
  [Operator.MATCHES]: 'matches',
  [Operator.SIMILARITY]: 'is similar to',
  [Operator.STARTSWITH_TEXT]: 'starts with',
  [Operator.SUBSTRUCTURE]: 'has substructure',
  [Operator.CURRENT_USER]: 'is the current user',
  [Operator.RELATIVE_RANGE]: 'custom range',
  [Operator.LAST_WEEK]: 'in the last week',
  [Operator.NEXT_WEEK]: 'in the next week',
  [Operator.LAST_MONTH]: 'in the last month',
  [Operator.NEXT_MONTH]: 'in the next month',
};

export const BOOLEAN_OPERATORS = [Operator.IS_TRUE, Operator.IS_FALSE];
export const VALUELESS_OPERATORS = [
  Operator.IS_EMPTY,
  Operator.IS_NOT_EMPTY,
  Operator.IS_TRUE,
  Operator.IS_FALSE,
  Operator.CURRENT_USER,
];

export enum StandardFilterDataType {
  INT = 'int',
  FLOAT = 'float',
  DATE = 'date',
  DATETIME = 'datetime',
  STRING = 'string',
  BOOL = 'bool',
  ENUM = 'enum',
  SINGLE_VALUE_ENTITY_LINK = 'single_value_entity_link',
  MULTI_VALUE_ENTITY_LINK = 'multi_value_entity_link',
  VARIABLE = 'variable',
}

// These filters conform to standard behavior for their associated data type.
// Unlike SINGLE_VALUE_FILTER_EYS and MULTI_VALUE_FILTER_KEYS, the cardinality
// and shape of the value property will depend on the operator chosen, so a
// separate type is required to cover that variance.
export const STANDARD_FILTER_KEYS = [
  FilterKey.ANNOTATION,
  FilterKey.API_IDS,
  FilterKey.ASSAY_METHOD_NAME,
  FilterKey.ASSAY_RUN_CONDITION_NAMES,
  FilterKey.ASSAY_RUN_REPLICATE_NAMES,
  FilterKey.BARCODES,
  FilterKey.BATCH_TAG,
  FilterKey.BIOENTITY_LABELS,
  FilterKey.CONNECTION_GATEWAY,
  FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE,
  FilterKey.CREATED_AT,
  FilterKey.DILUTION_FACTOR,
  FilterKey.EXPIRATION_DATE,
  FilterKey.FREEZE_THAW_COUNT,
  FilterKey.GRID_EMPTY_CONTAINERS,
  FilterKey.INVENTORY_VALIDATION_ERROR_LABEL,
  FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION,
  FilterKey.IS_EXPIRED,
  FilterKey.IS_FILL_IN_ENTRY,
  FilterKey.LINEAGE_PARENT_API_IDENTIFIER,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.OWNER,
  FilterKey.OWNER_OR_COLLABORATOR,
  FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER,
  // responsibleTeam is no longer used, but keeping the constant here for backwards compatibility
  // we now use responsibleParties instead
  FilterKey.PROCESS_RESPONSIBLE_TEAM,
  FilterKey.PROCESS_TASK_ASSIGNEE,
  FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES,
  FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
  FilterKey.PROCESS_WATCHER,
  FilterKey.PUBLISHING_STATUS,
  FilterKey.RECIPE_METHOD_NAME,
  FilterKey.RECIPE_RUN_CONDITION_NAMES,
  FilterKey.RECIPE_RUN_REPLICATE_NAMES,
  FilterKey.REQUEST_ASSIGNEE,
  FilterKey.REQUEST_IDS,
  FilterKey.REQUEST_V2_SUBMISSION,
  FilterKey.REQUEST_V2_SUBMISSION_STATUS,
  FilterKey.REVIEW_COMPLETED,
  FilterKey.REVIEW_INITIAL_STARTED,
  FilterKey.REVIEW_LIFECYCLE,
  FilterKey.REVIEW_STARTED,
  FilterKey.ROOT_PROCESS_CREATOR,
  FilterKey.ROOT_PROCESS_WATCHER,
  FilterKey.SAMPLE,
  FilterKey.SAMPLE_AUTHORIZED_USERS,
  FilterKey.SAMPLE_CONTAINER,
  FilterKey.SAMPLE_DEFINITION,
  FilterKey.SAMPLE_OWNERS,
  FilterKey.SCHEMA_FIELD,
  FilterKey.SCHEMA_VALIDATION_ERROR_LABEL,
  FilterKey.TEST_STATUS,
] as const;
export type StandardFilterKey = (typeof STANDARD_FILTER_KEYS)[number];

const DATE_OPERATORS: Array<Operator> = [
  Operator.BETWEEN,
  Operator.GTE,
  Operator.LTE,
  Operator.EQ,
  Operator.RELATIVE_RANGE,
];

const NUMERIC_OPERATORS: Array<Operator> = [
  // Using EQ instead of IS for numeric filters to match saved searches and saved links
  Operator.EQ,
  Operator.BETWEEN,
  Operator.GTE,
  Operator.LTE,
];

const SINGLE_VALUE_ENTITY_LINK_OPERATORS: Array<Operator> = [Operator.IS_ONE_OF, Operator.IS_NONE_OF];
const MULTI_VALUE_ENTITY_LINK_OPERATORS: Array<Operator> = [
  Operator.HAS_ONE_OF,
  Operator.HAS_ALL_OF,
  Operator.HAS_NONE_OF,
];

export const NULLABLE_OPERATORS = [Operator.IS_EMPTY, Operator.IS_NOT_EMPTY];

const DATA_TYPE_TO_OPERATORS: {[key in StandardFilterDataType]: Array<Operator>} = {
  [StandardFilterDataType.INT]: NUMERIC_OPERATORS,
  [StandardFilterDataType.FLOAT]: NUMERIC_OPERATORS,
  [StandardFilterDataType.DATE]: DATE_OPERATORS,
  [StandardFilterDataType.DATETIME]: DATE_OPERATORS,
  [StandardFilterDataType.STRING]: [
    Operator.INCLUDES_TEXT,
    Operator.EXCLUDES_TEXT,
    Operator.STARTSWITH_TEXT,
    Operator.IS,
    Operator.IS_NOT,
    Operator.IS_ONE_OF,
    Operator.IS_NONE_OF,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [StandardFilterDataType.BOOL]: [Operator.IS_TRUE, Operator.IS_FALSE],
  [StandardFilterDataType.ENUM]: [Operator.IS_ONE_OF],
  [StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK]: SINGLE_VALUE_ENTITY_LINK_OPERATORS,
  [StandardFilterDataType.MULTI_VALUE_ENTITY_LINK]: MULTI_VALUE_ENTITY_LINK_OPERATORS,
  [StandardFilterDataType.VARIABLE]: [Operator.IS_ONE_OF], // This is just a placeholder until the actual operators are set.
};

export type PrimitiveFilterValue = number | string | null;

export interface StandardRangeOperatorValue {
  from: PrimitiveFilterValue;
  to: PrimitiveFilterValue;
}

export enum RelativeRangeUnits {
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
  YEARS = 'years',
}

export interface StandardRelativeRangeOperatorValue {
  from: number | null;
  fromUnits: RelativeRangeUnits | null;
  to: number | null;
  toUnits: RelativeRangeUnits | null;
}

export type StandardFilterValue =
  | PrimitiveFilterValue
  | Array<PrimitiveFilterValue>
  | StandardRangeOperatorValue
  | StandardRelativeRangeOperatorValue;

export const isStandardFilterScalarValue = (value: StandardFilterValue): value is PrimitiveFilterValue =>
  ['number', 'string'].includes(typeof value);

export const isStandardFilterArrayValue = (
  value: StandardFilterValue
): value is Array<PrimitiveFilterValue> => Array.isArray(value);

export const isStandardFilterRangeValue = (value: StandardFilterValue): value is StandardRangeOperatorValue =>
  value != null &&
  !isStandardFilterScalarValue(value) &&
  !isStandardFilterArrayValue(value) &&
  !value.hasOwnProperty('fromUnits');

export const isStandardFilterRelativeRangeValue = (
  value: StandardFilterValue
): value is StandardRelativeRangeOperatorValue =>
  value != null &&
  !isStandardFilterScalarValue(value) &&
  !isStandardFilterArrayValue(value) &&
  value.hasOwnProperty('fromUnits');

function standardFilterValuelessDefault(): StandardFilterValue {
  return null;
}

function standardFilterScalarDefault(): StandardFilterValue {
  return null;
}

function standardFilterArrayDefault(): StandardFilterValue {
  return [];
}

function standardFilterRangeDefault(): StandardFilterValue {
  return {from: null, to: null};
}

function standardFilterRelativeRangeDefault(): StandardFilterValue {
  return {from: null, fromUnits: RelativeRangeUnits.DAYS, to: null, toUnits: null};
}

function standardFilterLastWeekDefault(): StandardFilterValue {
  return {from: 1, fromUnits: RelativeRangeUnits.WEEKS, to: null, toUnits: null};
}

function standardFilterNextWeekDefault(): StandardFilterValue {
  return {from: null, fromUnits: null, to: 1, toUnits: RelativeRangeUnits.WEEKS};
}

function standardFilterLastMonthDefault(): StandardFilterValue {
  return {from: 1, fromUnits: RelativeRangeUnits.MONTHS, to: null, toUnits: null};
}

function standardFilterNextMonthDefault(): StandardFilterValue {
  return {from: null, fromUnits: null, to: 1, toUnits: RelativeRangeUnits.MONTHS};
}

export const STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY: {[key in Operator]?: () => StandardFilterValue} =
  {
    [Operator.EQ]: standardFilterScalarDefault,
    [Operator.IS]: standardFilterScalarDefault,
    [Operator.IS_ONE_OF]: standardFilterArrayDefault,
    [Operator.IS_NOT]: standardFilterScalarDefault,
    [Operator.IS_NONE_OF]: standardFilterArrayDefault,
    [Operator.GTE]: standardFilterScalarDefault,
    [Operator.LTE]: standardFilterScalarDefault,
    [Operator.BETWEEN]: standardFilterRangeDefault,
    [Operator.RELATIVE_RANGE]: standardFilterRelativeRangeDefault,
    [Operator.IS_EMPTY]: standardFilterValuelessDefault,
    [Operator.IS_NOT_EMPTY]: standardFilterValuelessDefault,
    [Operator.IS_TRUE]: standardFilterValuelessDefault,
    [Operator.IS_FALSE]: standardFilterValuelessDefault,
    [Operator.CURRENT_USER]: standardFilterValuelessDefault,
    [Operator.INCLUDES_TEXT]: standardFilterArrayDefault,
    [Operator.EXCLUDES_TEXT]: standardFilterArrayDefault,
    [Operator.STARTSWITH_TEXT]: standardFilterArrayDefault,
    [Operator.HAS_ONE_OF]: standardFilterArrayDefault,
    [Operator.HAS_NONE_OF]: standardFilterArrayDefault,
    [Operator.HAS_ALL_OF]: standardFilterArrayDefault,
    [Operator.HAS_ONLY]: standardFilterArrayDefault,
    [Operator.LAST_WEEK]: standardFilterLastWeekDefault,
    [Operator.NEXT_WEEK]: standardFilterNextWeekDefault,
    [Operator.LAST_MONTH]: standardFilterLastMonthDefault,
    [Operator.NEXT_MONTH]: standardFilterNextMonthDefault,
  };

export type FilterConstants = {
  [key in FilterKey]: {
    key: key;
    prefix?: string;
    name?: string;
    dependencies?: Array<FilterKey>;
    // These are required for standard filter keys.
    dataType?: StandardFilterDataType;
    isNullable?: boolean;
    // This is required for standard entity link filter keys.
    entityLinkTypes?: Array<ModelTypeConstants>;
    searchInputPlaceholderOverride?: string;
  };
};

// Keep filter prefixes in sync with api/lib/search/search_params.py.
export const FILTERS: FilterConstants = {
  [FilterKey.AA_PART_SEQUENCE]: {
    key: FilterKey.AA_PART_SEQUENCE,
    name: 'AA part sequences',
    prefix: 'aaPartSequence:',
  },
  [FilterKey.ACCOUNT_STATUS]: {
    key: FilterKey.ACCOUNT_STATUS,
    prefix: 'accountStatus:',
    name: 'Account status',
  },
  [FilterKey.ANY_LINK_SCHEMA_FIELD]: {
    key: FilterKey.ANY_LINK_SCHEMA_FIELD,
    prefix: 'linkedItems:',
    name: 'Linked items',
  },
  [FilterKey.ANCESTOR_FOLDER]: {
    key: FilterKey.ANCESTOR_FOLDER,
    prefix: 'ancestorFolder:',
    name: 'Folder',
  },
  [FilterKey.ANCESTOR_STORABLE]: {
    key: FilterKey.ANCESTOR_STORABLE,
    prefix: 'ancestorStorable:',
    name: 'Stored within',
  },
  [FilterKey.ANNOTATION]: {
    key: FilterKey.ANNOTATION,
    prefix: 'annotations:',
    name: 'Annotation name',
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.API_IDS]: {
    key: FilterKey.API_IDS,
    name: 'API ID',
    prefix: 'APIIds:',
    // This is a special case where we want the filter form to be string type, but we're handling
    // everything else, including the backend as a single value entity link type.
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.ARCHIVE_PURPOSES]: {
    key: FilterKey.ARCHIVE_PURPOSES,
    prefix: 'archivePurposes:',
    name: 'Archived',
  },
  [FilterKey.ARCHIVE_STATUS]: {
    key: FilterKey.ARCHIVE_STATUS,
    prefix: 'archiveStatus:',
  },
  [FilterKey.ASSAY_SCHEMA]: {
    key: FilterKey.ASSAY_SCHEMA,
    prefix: 'assaySchema:',
  },
  [FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS]: {
    key: FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS,
    name: processExecutionTypeDisplayName(ProcessClientOrigin.ASSAYS, ProcessExecutionType.PROCEDURE),
    prefix: 'assayRunTasks:',
  },
  [FilterKey.ASSAY_METHOD_NAME]: {
    key: FilterKey.ASSAY_METHOD_NAME,
    prefix: 'assayMethodName:',
    name: `${processExecutionTypeDisplayName(
      ProcessClientOrigin.ASSAYS,
      ProcessExecutionType.PROCEDURE_METHOD
    )} name`,
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.ASSAY_RUN_CONDITION_NAMES]: {
    key: FilterKey.ASSAY_RUN_CONDITION_NAMES,
    prefix: 'assayRunConditionNames:',
    name: `Assay run`,
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.ASSAY_RUN_REPLICATE_NAMES]: {
    key: FilterKey.ASSAY_RUN_REPLICATE_NAMES,
    prefix: 'assayRunReplicateNames:',
    name: `Assay run replicate`,
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.AUTHOR]: {
    key: FilterKey.AUTHOR,
    prefix: 'authors:',
    name: 'Author',
  },
  [FilterKey.BARCODES]: {
    key: FilterKey.BARCODES,
    prefix: 'barcodes:',
    name: 'Barcode',
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.BATCH_TAG]: {
    key: FilterKey.BATCH_TAG,
    name: 'Batch field',
  },
  [FilterKey.BIOENTITY_LABELS]: {
    key: FilterKey.BIOENTITY_LABELS,
    prefix: 'bioentityLabels:',
    name: 'Name, ID, or alias',
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.BLAST]: {
    key: FilterKey.BLAST,
    prefix: 'blast:',
    name: 'BLAST',
  },
  [FilterKey.CAPABILITIES]: {
    key: FilterKey.CAPABILITIES,
    prefix: 'capabilities:',
    name: 'Application access',
  },
  [FilterKey.CHEMICAL_STRUCTURE]: {
    key: FilterKey.CHEMICAL_STRUCTURE,
    prefix: 'chemicalStructure:',
    name: 'Chemical structure',
  },
  [FilterKey.CONNECTION]: {
    key: FilterKey.CONNECTION,
    prefix: 'connection:',
    name: 'Connection',
  },
  [FilterKey.CONNECTION_ADAPTER]: {
    key: FilterKey.CONNECTION_ADAPTER,
    prefix: 'connectionAdapter:',
    name: 'Connection Adapter',
  },
  [FilterKey.CONNECTION_GATEWAY]: {
    key: FilterKey.CONNECTION_GATEWAY,
    prefix: 'connectionGateway:',
    name: 'Gateway',
    dataType: StandardFilterDataType.ENUM,
    isNullable: false,
  },
  [FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE]: {
    key: FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE,
    prefix: 'containerCheckOutAssignee:',
    name: 'Check-out assignee',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    isNullable: false,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.TEAM],
  },
  [FilterKey.CONTAINER_CHECK_OUT_STATUS]: {
    key: FilterKey.CONTAINER_CHECK_OUT_STATUS,
    prefix: 'containerCheckOutStatus:',
    name: 'Check-out status',
  },
  [FilterKey.CONTAINER_CONCENTRATION]: {
    key: FilterKey.CONTAINER_CONCENTRATION,
    prefix: 'containerConcentration:',
    name: 'Concentration',
  },
  [FilterKey.CONTAINER_CONTENTS]: {
    key: FilterKey.CONTAINER_CONTENTS,
    prefix: 'containerContents:',
    name: 'Contents',
  },
  [FilterKey.CONTAINER_CONTENT_SCHEMA]: {
    key: FilterKey.CONTAINER_CONTENT_SCHEMA,
    prefix: 'containerContentSchema:',
    name: 'Content type',
  },
  // Should be deprecated, use CONTAINER_CONTENTS instead
  [FilterKey.CONTAINER_PARENT]: {
    key: FilterKey.CONTAINER_PARENT,
    prefix: 'containerParent:',
    name: 'Container contents',
  },
  [FilterKey.CONTAINER_QUANTITY]: {
    key: FilterKey.CONTAINER_QUANTITY,
    prefix: 'containerVolume:',
    name: getQuantityDisplayString(),
  },
  [FilterKey.CREATED_AT]: {
    key: FilterKey.CREATED_AT,
    prefix: 'createdAt:',
    name: 'Created',
    dataType: StandardFilterDataType.DATETIME,
    isNullable: false,
  },
  [FilterKey.CREATION_ORIGIN_ENTRY]: {
    key: FilterKey.CREATION_ORIGIN_ENTRY,
    prefix: 'originEntry:',
    name: 'Origin entry',
  },
  [FilterKey.CREATOR]: {
    key: FilterKey.CREATOR,
    prefix: 'creator:',
    name: 'Creator',
  },
  [FilterKey.CUSTOM_TAG]: {
    key: FilterKey.CUSTOM_TAG,
    prefix: 'customTag:',
    name: 'Custom field',
  },
  [FilterKey.ENTRY_TEMPLATE]: {
    key: FilterKey.ENTRY_TEMPLATE,
    prefix: 'entryTemplate:',
    name: 'Entry template',
  },
  [FilterKey.FOLDER]: {
    key: FilterKey.FOLDER,
    prefix: 'folder:',
    name: 'Folder',
  },
  [FilterKey.FOLDER_SECTION]: {
    key: FilterKey.FOLDER_SECTION,
    prefix: 'section:',
    name: 'Section',
  },
  [FilterKey.GRID_EMPTY_CONTAINERS]: {
    key: FilterKey.GRID_EMPTY_CONTAINERS,
    prefix: 'gridEmptyContainers:',
    name: 'Empty containers',
    dataType: StandardFilterDataType.INT,
    isNullable: false,
  },
  [FilterKey.GRID_EMPTY_POSITIONS]: {
    key: FilterKey.GRID_EMPTY_POSITIONS,
    prefix: 'gridEmptyPositions:',
    name: 'Empty positions',
  },
  [FilterKey.GROUP_BY_KEY]: {
    key: FilterKey.GROUP_BY_KEY,
    prefix: 'groupByKey:',
    name: 'Group by key',
  },
  [FilterKey.HIDDEN]: {
    key: FilterKey.HIDDEN,
    prefix: 'hidden:',
    name: 'Hidden',
  },
  [FilterKey.INGREDIENTS]: {
    key: FilterKey.INGREDIENTS,
    prefix: 'ingredients:',
    name: 'Ingredients',
  },
  [FilterKey.INSTITUTION]: {
    key: FilterKey.INSTITUTION,
    prefix: 'institution:',
    name: 'Institution',
  },
  [FilterKey.INVENTORY_VALIDATION_ERROR_LABEL]: {
    key: FilterKey.INVENTORY_VALIDATION_ERROR_LABEL,
    prefix: 'inventoryValidationErrorLabels:',
    name: 'Failure reason',
    dataType: StandardFilterDataType.ENUM,
    isNullable: false,
  },
  [FilterKey.INVENTORY_VALIDATION_STATUS]: {
    key: FilterKey.INVENTORY_VALIDATION_STATUS,
    prefix: 'inventoryValidationStatuses:',
    name: 'Status',
  },
  [FilterKey.ITEM_IN_WORKLIST]: {
    key: FilterKey.ITEM_IN_WORKLIST,
    prefix: 'inWorklist:',
    name: 'In worklist',
  },
  [FilterKey.IN_STUDY]: {
    key: FilterKey.IN_STUDY,
    prefix: 'inStudy:',
    name: 'In study',
  },
  [FilterKey.MODIFIED_AT]: {
    key: FilterKey.MODIFIED_AT,
    prefix: 'modifiedAt:',
    name: 'Modified',
    dataType: StandardFilterDataType.DATETIME,
    isNullable: false,
  },
  [FilterKey.MONOMER]: {
    key: FilterKey.MONOMER,
    prefix: 'monomer:',
    name: 'Monomer',
  },
  [FilterKey.NOTE_REFERENCE]: {
    key: FilterKey.NOTE_REFERENCE,
    prefix: 'noteReference:',
    name: 'Mentions',
  },
  [FilterKey.OLIGO_HELM_DNA]: {
    key: FilterKey.OLIGO_HELM_DNA,
    prefix: 'dnaHelm:',
    name: 'DNA HELM string',
  },
  [FilterKey.OLIGO_HELM_RNA]: {
    key: FilterKey.OLIGO_HELM_RNA,
    prefix: 'rnaHelm:',
    name: 'RNA HELM string',
  },
  [FilterKey.ORGANIZATION]: {
    key: FilterKey.ORGANIZATION,
    name: 'Organization',
  },
  [FilterKey.OWNER]: {
    key: FilterKey.OWNER,
    name: 'Owner',
    prefix: 'owner:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.ORGANIZATION],
    isNullable: false,
  },
  [FilterKey.OWNER_OR_COLLABORATOR]: {
    key: FilterKey.OWNER_OR_COLLABORATOR,
    name: 'Owner or collaborator',
    prefix: 'ownerOrCollaborator:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.TEAM, ModelTypeConstants.ORGANIZATION],
    isNullable: false,
  },
  [FilterKey.PARENT_STORABLE]: {
    key: FilterKey.PARENT_STORABLE,
    prefix: 'parentStorable:',
  },
  [FilterKey.PART_SEQUENCE]: {
    key: FilterKey.PART_SEQUENCE,
    name: 'DNA part sequences',
    prefix: 'partSequence:',
  },
  [FilterKey.POLICY]: {
    key: FilterKey.POLICY,
    name: 'Policy',
    prefix: 'policy:',
  },
  [FilterKey.RNA_PART_SEQUENCE]: {
    key: FilterKey.RNA_PART_SEQUENCE,
    name: 'RNA part sequences',
    prefix: 'rnaPartSequence:',
  },
  [FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS]: {
    key: FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS,
    name: processExecutionTypeDisplayName(ProcessClientOrigin.RECIPES, ProcessExecutionType.PROCEDURE),
    prefix: 'procedureRunTasks:',
  },
  [FilterKey.RECIPE_METHOD_NAME]: {
    key: FilterKey.RECIPE_METHOD_NAME,
    prefix: 'procedureMethodName:',
    name: `${processExecutionTypeDisplayName(
      ProcessClientOrigin.RECIPES,
      ProcessExecutionType.PROCEDURE_METHOD
    )} name`,
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.RECIPE_RUN_CONDITION_NAMES]: {
    key: FilterKey.RECIPE_RUN_CONDITION_NAMES,
    prefix: 'procedureRunConditionNames:',
    name: `Condition`,
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.RECIPE_RUN_REPLICATE_NAMES]: {
    key: FilterKey.RECIPE_RUN_REPLICATE_NAMES,
    prefix: 'procedureRunReplicateNames:',
    name: 'Recipe replicate',
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.PROCEDURE_TASK_STATUS]: {
    key: FilterKey.PROCEDURE_TASK_STATUS,
    prefix: 'procedureTaskStatus:',
    name: 'Status',
  },
  // TODO(BNCH-96786): Update the Assay / Recipe / Procedure template strings to match the new naming conventions.
  [FilterKey.ASSAY_TEMPLATE]: {
    key: FilterKey.ASSAY_TEMPLATE,
    prefix: 'assayTemplate:',
    name: 'Assay',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.PROCEDURE],
    isNullable: false,
  },
  [FilterKey.RECIPE_TEMPLATE]: {
    key: FilterKey.RECIPE_TEMPLATE,
    prefix: 'recipeTemplate:',
    name: 'Recipe',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.PROCEDURE],
    isNullable: false,
  },
  [FilterKey.PROCESS]: {
    key: FilterKey.PROCESS,
    prefix: 'taskGroup:',
    name: 'Task group',
  },
  [FilterKey.PROCESS_CLIENT_ORIGIN]: {
    key: FilterKey.PROCESS_CLIENT_ORIGIN,
    prefix: 'view:',
    name: 'View',
  },
  [FilterKey.PROCESS_EXECUTION_TYPE]: {
    key: FilterKey.PROCESS_EXECUTION_TYPE,
    prefix: 'executionType:',
    name: 'Execution type',
  },
  // responsibleTeam is no longer used, but keeping the constant here for backwards compatibility
  // we now use responsibleParties instead
  [FilterKey.PROCESS_RESPONSIBLE_TEAM]: {
    key: FilterKey.PROCESS_RESPONSIBLE_TEAM,
    prefix: 'responsibleTeam:',
    name: 'Responsible team',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.TEAM],
    isNullable: true,
  },
  [FilterKey.PROCESS_STATUS]: {
    key: FilterKey.PROCESS_STATUS,
    prefix: 'status:',
    name: 'Status',
  },
  [FilterKey.PROCESS_TASK]: {
    key: FilterKey.PROCESS_TASK,
    prefix: 'workflowTask:',
    name: 'Task',
  },
  [FilterKey.PROCESS_TASK_ASSIGNEE]: {
    key: FilterKey.PROCESS_TASK_ASSIGNEE,
    prefix: 'assignee:',
    name: 'Assignee',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    isNullable: true,
    entityLinkTypes: [ModelTypeConstants.USER],
  },
  [FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE]: {
    key: FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
    prefix: 'scheduledOnDate:',
    name: 'Scheduled on',
    dataType: StandardFilterDataType.DATE,
    isNullable: true,
  },
  [FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY]: {
    key: FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY,
    prefix: 'executionEntry:',
    name: 'Entry',
  },
  [FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS]: {
    key: FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS,
    prefix: 'hasFailedMappings:',
    name: 'Failed mapping',
  },
  [FilterKey.PROCESS_TASK_STATUS]: {
    key: FilterKey.PROCESS_TASK_STATUS,
    prefix: 'taskStatus:',
    name: 'Status',
  },
  [FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES]: {
    key: FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES,
    prefix: 'taskStatusForAllTaskTypes:',
    name: 'Status',
  },
  [FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES]: {
    key: FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES,
    prefix: 'taskResponsibleParties:',
    name: 'Responsible parties',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.TEAM],
    isNullable: true,
  },
  [FilterKey.PROCESS_WATCHER]: {
    key: FilterKey.PROCESS_WATCHER,
    prefix: 'watcher:',
    name: 'Watcher',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.USER],
    isNullable: true,
  },
  [FilterKey.PROJECT]: {
    key: FilterKey.PROJECT,
  },
  [FilterKey.PROJECT_ATTRIBUTE]: {
    key: FilterKey.PROJECT_ATTRIBUTE,
    name: 'Attribute',
  },
  [FilterKey.PROJECT_FOLDER]: {
    key: FilterKey.PROJECT_FOLDER,
    prefix: 'projectFolder:',
    name: 'Folder',
  },
  [FilterKey.PROTEIN_AMINO_ACIDS]: {
    key: FilterKey.PROTEIN_AMINO_ACIDS,
    prefix: 'aminoAcids:',
    name: 'AA string',
  },
  [FilterKey.PROTEIN_PROSITE]: {
    key: FilterKey.PROTEIN_PROSITE,
    prefix: 'prosite:',
    name: 'PROSITE pattern',
  },
  [FilterKey.ALPHAFOLD_STRUCTURE]: {
    key: FilterKey.ALPHAFOLD_STRUCTURE,
    prefix: 'proteinStructure:',
    name: 'Has AlphaFold structure',
  },
  [FilterKey.REGISTRY]: {
    key: FilterKey.REGISTRY,
    prefix: 'registry:',
  },
  [FilterKey.REGISTRY_COLLECTION_OWNER]: {
    key: FilterKey.REGISTRY_COLLECTION_OWNER,
    prefix: 'registryCollectionOwner:',
    name: 'Collection',
  },
  [FilterKey.SAMPLE]: {
    key: FilterKey.SAMPLE,
    name: 'Sample',
    prefix: 'sample:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [
      ModelTypeConstants.BASIC_FOLDER_ITEM,
      ModelTypeConstants.MIXTURE,
      ModelTypeConstants.MOLECULE,
      ModelTypeConstants.SEQUENCE,
      ModelTypeConstants.PROTEIN,
    ],
    isNullable: false,
    searchInputPlaceholderOverride: 'Search for a trial sample',
  },
  [FilterKey.SAMPLE_RESTRICTION_ACCESS]: {
    key: FilterKey.SAMPLE_RESTRICTION_ACCESS,
    prefix: 'sampleRestrictionAccess:',
    name: 'Restriction access',
  },
  [FilterKey.SAMPLE_RESTRICTION_STATUS]: {
    key: FilterKey.SAMPLE_RESTRICTION_STATUS,
    prefix: 'sampleRestrictionStatus:',
    name: 'Restriction status',
  },
  [FilterKey.REVIEW]: {
    key: FilterKey.REVIEW,
    prefix: 'rev:',
    name: 'Review status',
  },
  [FilterKey.REVIEW_LIFECYCLE]: {
    key: FilterKey.REVIEW_LIFECYCLE,
    prefix: 'reviewLifecycle:',
    name: 'Review process',
    dataType: StandardFilterDataType.ENUM,
    isNullable: true,
  },
  [FilterKey.REVIEW_LIFECYCLE_TYPE]: {
    key: FilterKey.REVIEW_LIFECYCLE_TYPE,
    prefix: 'reviewLifecycleType:',
    name: 'Review process type',
  },
  [FilterKey.REQUEST_ASSIGNEE]: {
    key: FilterKey.REQUEST_ASSIGNEE,
    prefix: 'requestAssignee:',
    name: 'Assignee',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.TEAM],
    isNullable: true,
  },
  [FilterKey.REQUEST_REQUESTOR]: {
    key: FilterKey.REQUEST_REQUESTOR,
    prefix: 'requestor:',
    name: 'Requestor',
  },
  [FilterKey.REQUEST_STATUS]: {
    key: FilterKey.REQUEST_STATUS,
    prefix: 'requestStatus:',
    name: 'Status',
  },
  [FilterKey.REQUEST_TASK_SCHEMA]: {
    key: FilterKey.REQUEST_TASK_SCHEMA,
    prefix: 'requestTaskSchema:',
    name: 'Task',
  },
  [FilterKey.RUN_AVAILABILITY]: {
    key: FilterKey.RUN_AVAILABILITY,
    prefix: 'runAvailability:',
  },
  [FilterKey.SCHEMA_FIELD]: {
    key: FilterKey.SCHEMA_FIELD,
    prefix: 'field:',
    name: 'Field',
    // This is only a placeholder used for creating an empty filter
    // Since the data type depends on the schema field
    dataType: StandardFilterDataType.VARIABLE,
    isNullable: true,
  },
  [FilterKey.SCHEMA]: {
    key: FilterKey.SCHEMA,
    prefix: 'schema:',
  },
  [FilterKey.SCHEMA_INTERFACE]: {
    key: FilterKey.SCHEMA_INTERFACE,
    prefix: 'schemaInterface:',
  },
  [FilterKey.SCHEMA_VALIDATION_ERROR_LABEL]: {
    key: FilterKey.SCHEMA_VALIDATION_ERROR_LABEL,
    prefix: 'schemaValidationErrorLabels:',
    name: 'Failure reason',
    dataType: StandardFilterDataType.ENUM,
    isNullable: false,
  },
  [FilterKey.SCHEMA_VALIDATION_STATUS]: {
    key: FilterKey.SCHEMA_VALIDATION_STATUS,
    prefix: 'schemaValidationStatuses:',
    name: 'Status',
  },
  [FilterKey.REGISTRATION_STATUS]: {
    key: FilterKey.REGISTRATION_STATUS,
    prefix: 'registrationStatus:',
    name: 'Registration status',
  },
  [FilterKey.ROOT_PROCESS]: {
    key: FilterKey.ROOT_PROCESS,
    prefix: 'rootProcess:',
    name: 'Flowchart task group',
  },
  [FilterKey.ROOT_PROCESS_TASK_SCHEMA]: {
    key: FilterKey.ROOT_PROCESS_TASK_SCHEMA,
    prefix: 'flowchartTaskSchema:',
    name: 'Flowchart task schema',
  },
  [FilterKey.ROOT_PROCESS_CREATOR]: {
    key: FilterKey.ROOT_PROCESS_CREATOR,
    prefix: 'flowchartCreator:',
    name: 'Flowchart creator',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    isNullable: false,
    entityLinkTypes: [ModelTypeConstants.USER],
  },
  [FilterKey.ROOT_PROCESS_WATCHER]: {
    key: FilterKey.ROOT_PROCESS_WATCHER,
    prefix: 'flowchartWatcher:',
    name: 'Flowchart watcher',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    isNullable: true,
    entityLinkTypes: [ModelTypeConstants.USER],
  },
  [FilterKey.SAMPLE_AUTHORIZED_USERS]: {
    key: FilterKey.SAMPLE_AUTHORIZED_USERS,
    prefix: 'sampleAuthorizedUsers:',
    name: getRestrictedSampleUsersHeaderText(),
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    isNullable: true,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.TEAM, ModelTypeConstants.BENCHLING_APP],
  },
  [FilterKey.SAMPLE_OWNERS]: {
    key: FilterKey.SAMPLE_OWNERS,
    prefix: 'sampleOwners:',
    name: 'Sample owners',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    isNullable: true,
    entityLinkTypes: [ModelTypeConstants.USER, ModelTypeConstants.TEAM],
  },
  [FilterKey.SEQUENCE_BASES]: {
    key: FilterKey.SEQUENCE_BASES,
    prefix: 'bases:',
    name: 'Nucleotide string',
  },
  [FilterKey.SESSION_NAME]: {
    key: FilterKey.SESSION_NAME,
    prefix: 'sessionName:',
    name: 'Session name',
  },
  [FilterKey.SESSION_STATUS]: {
    key: FilterKey.SESSION_STATUS,
    prefix: 'sessionStatus:',
    name: 'Session status',
  },
  [FilterKey.SESSION_MODIFIED_AT]: {
    key: FilterKey.SESSION_MODIFIED_AT,
    prefix: 'sessionModifiedAt:',
    name: 'Session modified at',
  },
  [FilterKey.SHARED_WITH]: {
    key: FilterKey.SHARED_WITH,
    name: 'Shared with',
  },
  [FilterKey.STARRED]: {
    key: FilterKey.STARRED,
    prefix: 'starred:',
    name: 'Starred',
  },
  [FilterKey.STUDY_PHASE]: {
    key: FilterKey.STUDY_PHASE,
    prefix: 'studyPhase:',
    name: 'Phase',
  },
  [FilterKey.TAG]: {
    key: FilterKey.TAG,
    prefix: 'tag:',
    name: 'Field',
  },
  [FilterKey.TEAM]: {
    key: FilterKey.TEAM,
    name: 'Team',
  },
  [FilterKey.TOP_LEVEL_PERMISSIONS]: {
    key: FilterKey.TOP_LEVEL_PERMISSIONS,
    prefix: 'topLevelPermissions:',
    name: 'Top level permissions',
  },
  [FilterKey.TRANSCRIPTIONS]: {
    key: FilterKey.TRANSCRIPTIONS,
    prefix: 'transcriptions:',
    name: 'Transcriptions',
  },
  // TODO(T11608): Consolidate with TYPES filter
  [FilterKey.TYPE]: {
    key: FilterKey.TYPE,
    name: 'Entity type',
  },
  [FilterKey.TYPES]: {
    key: FilterKey.TYPES,
    prefix: 'types:',
    name: 'Type',
  },
  [FilterKey.WORKFLOW_STATUS]: {
    key: FilterKey.WORKFLOW_STATUS,
    prefix: 'workflowStatus:',
    name: 'Status',
  },
  [FilterKey.WORKFLOW_TEMPLATE]: {
    key: FilterKey.WORKFLOW_TEMPLATE,
    prefix: 'workflowTemplate:',
    name: 'Template',
  },
  [FilterKey.WORKLIST_TYPE]: {
    key: FilterKey.WORKLIST_TYPE,
    prefix: 'worklistType:',
    name: 'Worklist type',
  },
  [FilterKey.QUERY_API_BIOENTITY_ID]: {
    key: FilterKey.QUERY_API_BIOENTITY_ID,
    prefix: 'id:',
    // Called Name because the corresponding filter form is a bioentity input form where the user enters names
    // to find corresponding entities.
    // If/when we add a QUERY_API_NAMES filter, we should revisit this.
    name: 'Name',
  },
  [FilterKey.QUERY_API_ARCHIVE_REASON]: {
    key: FilterKey.QUERY_API_ARCHIVE_REASON,
    prefix: 'archiveStatus:',
    name: 'Archived',
  },
  [FilterKey.QUERY_API_MODIFIED_AT]: {
    key: FilterKey.QUERY_API_MODIFIED_AT,
    prefix: 'modifiedAt:',
    name: 'Modified',
  },
  [FilterKey.QUERY_API_CREATED_AT]: {
    key: FilterKey.QUERY_API_CREATED_AT,
    prefix: 'createdAt:',
    name: 'Created',
  },
  [FilterKey.QUERY_API_AUTHOR]: {
    key: FilterKey.QUERY_API_AUTHOR,
    prefix: 'author:',
    name: 'Author',
  },
  [FilterKey.QUERY_API_CREATOR]: {
    key: FilterKey.QUERY_API_CREATOR,
    prefix: 'creator:',
    name: 'Creator',
  },
  [FilterKey.QUERY_API_PROJECT]: {
    key: FilterKey.QUERY_API_PROJECT,
    prefix: 'project:',
    name: 'Project',
  },
  [FilterKey.QUERY_API_REGISTRY]: {
    key: FilterKey.QUERY_API_REGISTRY,
    prefix: 'registry:',
    name: 'Registry',
  },
  [FilterKey.REVIEW_COMPLETED]: {
    key: FilterKey.REVIEW_COMPLETED,
    name: 'Review completed',
    prefix: 'ReviewCompleted:',
    dataType: StandardFilterDataType.DATE,
    isNullable: true,
  },
  [FilterKey.REVIEW_STARTED]: {
    key: FilterKey.REVIEW_STARTED,
    name: 'Review last started',
    prefix: 'ReviewStarted:',
    dataType: StandardFilterDataType.DATE,
    isNullable: true,
  },
  [FilterKey.REVIEW_INITIAL_STARTED]: {
    key: FilterKey.REVIEW_INITIAL_STARTED,
    name: 'Review initially started',
    prefix: 'ReviewInitialStarted:',
    dataType: StandardFilterDataType.DATE,
    isNullable: true,
  },
  [FilterKey.EXPIRATION_DATE]: {
    key: FilterKey.EXPIRATION_DATE,
    name: 'Expiration date',
    prefix: 'ExpirationDate:',
    dataType: StandardFilterDataType.DATETIME,
    isNullable: true,
  },
  [FilterKey.IS_EXPIRED]: {
    key: FilterKey.IS_EXPIRED,
    name: 'Expired',
    prefix: 'expired:',
    dataType: StandardFilterDataType.BOOL,
    isNullable: false,
  },
  [FilterKey.NAMES]: {
    key: FilterKey.NAMES,
    prefix: 'names:',
    name: 'Name',
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.REQUEST_IDS]: {
    key: FilterKey.REQUEST_IDS,
    prefix: 'requestId:',
    name: `${getLegacyRequestObjectName()} ID`,
    dataType: StandardFilterDataType.STRING,
    isNullable: false,
  },
  [FilterKey.FREEZE_THAW_COUNT]: {
    key: FilterKey.FREEZE_THAW_COUNT,
    name: 'Freeze/thaw count',
    prefix: 'freezeThawCount:',
    dataType: StandardFilterDataType.INT,
    isNullable: true,
  },
  [FilterKey.REQUEST_V2_SUBMISSION]: {
    key: FilterKey.REQUEST_V2_SUBMISSION,
    name: 'Request',
    prefix: 'requestV2Submission:',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    isNullable: false,
  },
  [FilterKey.REQUEST_V2_SUBMISSION_STATUS]: {
    key: FilterKey.REQUEST_V2_SUBMISSION_STATUS,
    name: 'Request status',
    prefix: 'requestV2SubmissionStatus:',
    dataType: StandardFilterDataType.ENUM,
    isNullable: false,
  },
  [FilterKey.REQUEST_V2_DEFINITION]: {
    key: FilterKey.REQUEST_V2_DEFINITION,
    name: 'Request type',
    prefix: 'requestV2Definition:',
    dataType: StandardFilterDataType.MULTI_VALUE_ENTITY_LINK,
    isNullable: false,
    entityLinkTypes: [ModelTypeConstants.REQUEST_V2_DEFINITION],
  },
  [FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION]: {
    key: FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION,
    name: 'is unsubmitted',
    prefix: 'isAssociatedWithUnsubmittedRequestV2Submission:',
    dataType: StandardFilterDataType.BOOL,
    isNullable: false,
  },
  [FilterKey.TEST_STATUS]: {
    key: FilterKey.TEST_STATUS,
    name: 'Test status',
    prefix: 'testStatus:',
    dataType: StandardFilterDataType.ENUM,
    isNullable: false,
  },
  [FilterKey.DILUTION_FACTOR]: {
    key: FilterKey.DILUTION_FACTOR,
    name: 'Dilution factor',
    prefix: 'dilutionFactor:',
    dataType: StandardFilterDataType.INT,
    isNullable: true,
  },
  [FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER]: {
    key: FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER,
    name: 'Is processes system data',
    prefix: 'ProcessesIsSystemDataFilter:',
    dataType: StandardFilterDataType.BOOL,
    isNullable: false,
  },
  [FilterKey.SAMPLE_CONTAINER]: {
    key: FilterKey.SAMPLE_CONTAINER,
    name: 'Sample container',
    prefix: 'sampleContainer:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    entityLinkTypes: [ModelTypeConstants.CONTAINER],
    isNullable: true,
  },
  [FilterKey.SAMPLE_DEFINITION]: {
    key: FilterKey.SAMPLE_DEFINITION,
    name: 'Sample definition',
    prefix: 'sampleDefinition:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    isNullable: true,
    entityLinkTypes: [ModelTypeConstants.SAMPLE_DEFINITION],
  },
  [FilterKey.WORKSHEET]: {
    key: FilterKey.WORKSHEET,
    name: 'Worksheet',
    prefix: 'worksheet:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    isNullable: true,
    entityLinkTypes: [ModelTypeConstants.WORKSHEET],
  },
  [FilterKey.IS_FILL_IN_ENTRY]: {
    key: FilterKey.IS_FILL_IN_ENTRY,
    name: 'Fill-In Entry Filter',
    prefix: 'isFillInEntry:',
    dataType: StandardFilterDataType.BOOL,
    isNullable: false,
  },
  [FilterKey.LINEAGE_PARENT_API_IDENTIFIER]: {
    key: FilterKey.LINEAGE_PARENT_API_IDENTIFIER,
    name: 'Origin',
    prefix: 'lineageParentAPIIdentifier:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
    isNullable: false,
    entityLinkTypes: [
      ModelTypeConstants.CONNECTION,
      ModelTypeConstants.ANALYSIS,
      ModelTypeConstants.ASSAY_RUN,
      ModelTypeConstants.STUDY,
    ],
  },
  [FilterKey.TEMPLATE_COLLECTION]: {
    key: FilterKey.TEMPLATE_COLLECTION,
    name: 'Template Collection',
    prefix: 'templateCollection:',
    dataType: StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK,
  },
  [FilterKey.PUBLISHING_STATUS]: {
    key: FilterKey.PUBLISHING_STATUS,
    name: 'Publishing status',
    prefix: 'publishingStatus:',
    dataType: StandardFilterDataType.ENUM,
    isNullable: false,
  },
};

// This is unused currently, but is defined for use in the FILTER_KEY_TO_OPERATORS as standard
// keys get added, so that we do not have to duplicate the list of operators fromt he data type
// list.
export function standardOperatorsForKey(
  key: FilterKey,
  overideDataType?: StandardFilterDataType
): Array<Operator> {
  const dataType = overideDataType || FILTERS[key].dataType;
  if (!dataType) {
    throw new Error(`Attempted to find standard operators for ${key} but it has no dataType in FILTERS.`);
  }
  const operators = [...DATA_TYPE_TO_OPERATORS[dataType]];
  if (FILTERS[key].isNullable) {
    if (!operators.includes(Operator.IS_EMPTY)) {
      operators.push(Operator.IS_EMPTY);
    }
    if (!operators.includes(Operator.IS_NOT_EMPTY)) {
      operators.push(Operator.IS_NOT_EMPTY);
    }
  }
  if (FILTERS[key].entityLinkTypes?.includes(ModelTypeConstants.USER)) {
    operators.push(Operator.CURRENT_USER);
  }

  return operators;
}

export const FILTER_KEY_TO_OPERATORS: {[key in FilterKey]: Array<Operator>} = {
  [FilterKey.ANY_LINK_SCHEMA_FIELD]: [Operator.HAS_ALL_OF, Operator.HAS_ONE_OF, Operator.HAS_NONE_OF],
  [FilterKey.ACCOUNT_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.ANCESTOR_FOLDER]: [Operator.IS],
  [FilterKey.ANCESTOR_STORABLE]: [Operator.IS],
  [FilterKey.ANNOTATION]: standardOperatorsForKey(FilterKey.ANNOTATION),
  [FilterKey.API_IDS]: DATA_TYPE_TO_OPERATORS[StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK],
  [FilterKey.ARCHIVE_PURPOSES]: [Operator.IS_ONE_OF],
  [FilterKey.ARCHIVE_STATUS]: [Operator.IS],
  [FilterKey.ASSAY_SCHEMA]: [Operator.IS],
  [FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS]: [Operator.HAS_ONE_OF],
  [FilterKey.ASSAY_METHOD_NAME]: standardOperatorsForKey(FilterKey.ASSAY_METHOD_NAME),
  [FilterKey.ASSAY_RUN_CONDITION_NAMES]: standardOperatorsForKey(FilterKey.ASSAY_RUN_CONDITION_NAMES),
  [FilterKey.ASSAY_RUN_REPLICATE_NAMES]: standardOperatorsForKey(FilterKey.ASSAY_RUN_REPLICATE_NAMES),
  [FilterKey.AUTHOR]: [Operator.IS_ONE_OF],
  [FilterKey.BARCODES]: standardOperatorsForKey(FilterKey.BARCODES),
  [FilterKey.BATCH_TAG]: [],
  [FilterKey.BIOENTITY_LABELS]: standardOperatorsForKey(FilterKey.BIOENTITY_LABELS),
  [FilterKey.BLAST]: [Operator.BLASTN, Operator.BLASTP],
  [FilterKey.CAPABILITIES]: [Operator.IS_ONE_OF],
  [FilterKey.CONNECTION]: [Operator.IS_ONE_OF],
  [FilterKey.CONNECTION_ADAPTER]: [Operator.IS_ONE_OF],
  [FilterKey.CONNECTION_GATEWAY]: standardOperatorsForKey(FilterKey.CONNECTION_GATEWAY),
  [FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE]: standardOperatorsForKey(FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE),
  [FilterKey.CONTAINER_CHECK_OUT_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.CONTAINER_CONCENTRATION]: [Operator.EQ, Operator.LTE, Operator.GTE],
  [FilterKey.CONTAINER_CONTENTS]: [Operator.HAS_ONE_OF, Operator.HAS_ALL_OF, Operator.HAS_NONE_OF],
  [FilterKey.CONTAINER_CONTENT_SCHEMA]: [],
  [FilterKey.CONTAINER_PARENT]: [],
  [FilterKey.CONTAINER_QUANTITY]: [Operator.EQ, Operator.LTE, Operator.GTE],
  [FilterKey.CREATED_AT]: standardOperatorsForKey(FilterKey.CREATED_AT),
  [FilterKey.CREATION_ORIGIN_ENTRY]: [Operator.IS_ONE_OF],
  [FilterKey.CREATOR]: [Operator.IS_ONE_OF],
  [FilterKey.CUSTOM_TAG]: [Operator.IS, Operator.IS_EMPTY, Operator.IS_NOT_EMPTY],
  [FilterKey.ENTRY_TEMPLATE]: [Operator.IS_ONE_OF],
  [FilterKey.FOLDER]: [Operator.IS],
  [FilterKey.FOLDER_SECTION]: [Operator.IS],
  [FilterKey.GRID_EMPTY_CONTAINERS]: standardOperatorsForKey(FilterKey.GRID_EMPTY_CONTAINERS),
  [FilterKey.GRID_EMPTY_POSITIONS]: [Operator.EQ, Operator.LTE, Operator.GTE],
  [FilterKey.GROUP_BY_KEY]: [Operator.IS_ONE_OF],
  [FilterKey.HIDDEN]: [Operator.IS],
  [FilterKey.INGREDIENTS]: [Operator.HAS_ONE_OF, Operator.HAS_ALL_OF, Operator.HAS_NONE_OF],
  [FilterKey.INSTITUTION]: [Operator.IS],
  [FilterKey.INVENTORY_VALIDATION_ERROR_LABEL]: standardOperatorsForKey(
    FilterKey.INVENTORY_VALIDATION_ERROR_LABEL
  ),
  [FilterKey.INVENTORY_VALIDATION_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.ITEM_IN_WORKLIST]: [Operator.IS],
  [FilterKey.IN_STUDY]: [Operator.IS],
  [FilterKey.LINEAGE_PARENT_API_IDENTIFIER]: [Operator.IS_ONE_OF],
  [FilterKey.MODIFIED_AT]: standardOperatorsForKey(FilterKey.MODIFIED_AT),
  [FilterKey.CHEMICAL_STRUCTURE]: [Operator.SUBSTRUCTURE, Operator.SIMILARITY],
  [FilterKey.MONOMER]: [Operator.IS, Operator.IS_NOT],
  [FilterKey.NAMES]: standardOperatorsForKey(FilterKey.NAMES),
  [FilterKey.NOTE_REFERENCE]: [Operator.HAS_ONE_OF],
  [FilterKey.OLIGO_HELM_DNA]: [Operator.INCLUDES_TEXT, Operator.IS],
  [FilterKey.OLIGO_HELM_RNA]: [Operator.INCLUDES_TEXT, Operator.IS],
  [FilterKey.ORGANIZATION]: [Operator.IS],
  [FilterKey.OWNER]: standardOperatorsForKey(FilterKey.OWNER),
  [FilterKey.OWNER_OR_COLLABORATOR]: standardOperatorsForKey(FilterKey.OWNER_OR_COLLABORATOR),
  [FilterKey.PARENT_STORABLE]: [Operator.IS],
  [FilterKey.PART_SEQUENCE]: [Operator.HAS_ONE_OF],
  [FilterKey.POLICY]: [Operator.IS_ONE_OF],
  [FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS]: [Operator.HAS_ONE_OF],
  [FilterKey.RECIPE_METHOD_NAME]: standardOperatorsForKey(FilterKey.RECIPE_METHOD_NAME),
  [FilterKey.RECIPE_RUN_CONDITION_NAMES]: standardOperatorsForKey(FilterKey.RECIPE_RUN_CONDITION_NAMES),
  [FilterKey.RECIPE_RUN_REPLICATE_NAMES]: standardOperatorsForKey(FilterKey.RECIPE_RUN_REPLICATE_NAMES),
  [FilterKey.PROCEDURE_TASK_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.ASSAY_TEMPLATE]: [Operator.IS_ONE_OF],
  [FilterKey.RECIPE_TEMPLATE]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS_CLIENT_ORIGIN]: [Operator.IS],
  [FilterKey.PROCESS_EXECUTION_TYPE]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS_RESPONSIBLE_TEAM]: standardOperatorsForKey(FilterKey.PROCESS_RESPONSIBLE_TEAM),
  [FilterKey.PROCESS_STATUS]: [Operator.HAS_ONE_OF, Operator.HAS_ONLY, Operator.HAS_NONE_OF],
  [FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES]: standardOperatorsForKey(
    FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES
  ),
  [FilterKey.PROCESS_TASK]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS_TASK_ASSIGNEE]: standardOperatorsForKey(FilterKey.PROCESS_TASK_ASSIGNEE),
  [FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE]: standardOperatorsForKey(
    FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE
  ),
  [FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS]: [Operator.IS],
  [FilterKey.PROCESS_TASK_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES]: [Operator.IS_ONE_OF],
  [FilterKey.PROCESS_WATCHER]: standardOperatorsForKey(FilterKey.PROCESS_WATCHER),
  [FilterKey.PROJECT]: [],
  [FilterKey.PROJECT_ATTRIBUTE]: [Operator.IS],
  [FilterKey.PROJECT_FOLDER]: [Operator.IS],
  [FilterKey.PROTEIN_AMINO_ACIDS]: [Operator.INCLUDES_TEXT, Operator.IS_NOT_EMPTY, Operator.IS],
  [FilterKey.PROTEIN_PROSITE]: [],
  [FilterKey.ALPHAFOLD_STRUCTURE]: BOOLEAN_OPERATORS,
  [FilterKey.REGISTRY]: [Operator.IS],
  [FilterKey.REGISTRY_COLLECTION_OWNER]: [Operator.IS],
  [FilterKey.SAMPLE_RESTRICTION_ACCESS]: [Operator.IS_ONE_OF],
  [FilterKey.SAMPLE_RESTRICTION_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.REVIEW]: [Operator.IS],
  [FilterKey.REVIEW_LIFECYCLE]: standardOperatorsForKey(FilterKey.REVIEW_LIFECYCLE),
  [FilterKey.REVIEW_LIFECYCLE_TYPE]: [Operator.IS_ONE_OF],
  [FilterKey.REQUEST_ASSIGNEE]: standardOperatorsForKey(FilterKey.REQUEST_ASSIGNEE),
  [FilterKey.REQUEST_IDS]: standardOperatorsForKey(FilterKey.REQUEST_IDS),
  [FilterKey.REQUEST_REQUESTOR]: [Operator.IS_ONE_OF],
  [FilterKey.REQUEST_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.REQUEST_TASK_SCHEMA]: [Operator.IS],
  [FilterKey.RNA_PART_SEQUENCE]: [Operator.HAS_ONE_OF],
  [FilterKey.ROOT_PROCESS]: [Operator.IS_ONE_OF],
  [FilterKey.ROOT_PROCESS_TASK_SCHEMA]: [Operator.IS],
  [FilterKey.ROOT_PROCESS_CREATOR]: standardOperatorsForKey(FilterKey.ROOT_PROCESS_CREATOR),
  [FilterKey.ROOT_PROCESS_WATCHER]: standardOperatorsForKey(FilterKey.ROOT_PROCESS_WATCHER),
  [FilterKey.RUN_AVAILABILITY]: [Operator.IS],
  [FilterKey.SCHEMA]: [Operator.IS_ONE_OF],
  [FilterKey.SCHEMA_INTERFACE]: [Operator.IS_ONE_OF],
  // Each fieldType has its own set of operators
  [FilterKey.SCHEMA_FIELD]: [Operator.IS_ONE_OF],
  [FilterKey.SCHEMA_VALIDATION_ERROR_LABEL]: standardOperatorsForKey(FilterKey.SCHEMA_VALIDATION_ERROR_LABEL),
  [FilterKey.SCHEMA_VALIDATION_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.REGISTRATION_STATUS]: [Operator.IS],
  [FilterKey.SAMPLE_AUTHORIZED_USERS]: standardOperatorsForKey(FilterKey.SAMPLE_AUTHORIZED_USERS),
  [FilterKey.SAMPLE_OWNERS]: standardOperatorsForKey(FilterKey.SAMPLE_OWNERS),
  [FilterKey.SEQUENCE_BASES]: [Operator.INCLUDES_TEXT, Operator.IS, Operator.IS_EMPTY],
  [FilterKey.SESSION_NAME]: [Operator.CONTAINS],
  [FilterKey.SESSION_STATUS]: [Operator.IS_ONE_OF],
  [FilterKey.SESSION_MODIFIED_AT]: [Operator.BETWEEN],
  [FilterKey.SHARED_WITH]: [Operator.IS],
  [FilterKey.STARRED]: [Operator.IS],
  [FilterKey.STUDY_PHASE]: [Operator.IS_ONE_OF],
  // Each fieldType has its own set of operators
  [FilterKey.TAG]: [],
  [FilterKey.TEAM]: [Operator.IS],
  [FilterKey.TEMPLATE_COLLECTION]: standardOperatorsForKey(FilterKey.TEMPLATE_COLLECTION),
  [FilterKey.TOP_LEVEL_PERMISSIONS]: [Operator.IS],
  [FilterKey.TRANSCRIPTIONS]: [Operator.HAS_ONE_OF],
  [FilterKey.TYPE]: [],
  [FilterKey.TYPES]: [Operator.IS_ONE_OF],
  [FilterKey.WORKFLOW_STATUS]: [Operator.IS],
  [FilterKey.WORKFLOW_TEMPLATE]: [Operator.IS],
  [FilterKey.WORKLIST_TYPE]: [Operator.IS_ONE_OF],

  [FilterKey.QUERY_API_BIOENTITY_ID]: [Operator.IS_ONE_OF],
  [FilterKey.QUERY_API_ARCHIVE_REASON]: [Operator.IS_ONE_OF],
  [FilterKey.QUERY_API_MODIFIED_AT]: [],
  [FilterKey.QUERY_API_CREATED_AT]: [],
  [FilterKey.QUERY_API_AUTHOR]: [Operator.IS_ONE_OF],
  [FilterKey.QUERY_API_CREATOR]: [Operator.IS_ONE_OF],
  [FilterKey.QUERY_API_PROJECT]: [Operator.IS],
  [FilterKey.QUERY_API_REGISTRY]: [Operator.IS],
  [FilterKey.REVIEW_COMPLETED]: standardOperatorsForKey(FilterKey.REVIEW_COMPLETED),
  [FilterKey.REVIEW_STARTED]: standardOperatorsForKey(FilterKey.REVIEW_STARTED),
  [FilterKey.REVIEW_INITIAL_STARTED]: standardOperatorsForKey(FilterKey.REVIEW_INITIAL_STARTED),
  [FilterKey.EXPIRATION_DATE]: standardOperatorsForKey(FilterKey.EXPIRATION_DATE),
  [FilterKey.IS_EXPIRED]: standardOperatorsForKey(FilterKey.IS_EXPIRED),
  [FilterKey.FREEZE_THAW_COUNT]: standardOperatorsForKey(FilterKey.FREEZE_THAW_COUNT),
  [FilterKey.REQUEST_V2_SUBMISSION]: standardOperatorsForKey(FilterKey.REQUEST_V2_SUBMISSION),
  [FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION]: standardOperatorsForKey(
    FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION
  ),
  [FilterKey.DILUTION_FACTOR]: standardOperatorsForKey(FilterKey.DILUTION_FACTOR),
  [FilterKey.TEST_STATUS]: standardOperatorsForKey(FilterKey.TEST_STATUS),
  [FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER]: standardOperatorsForKey(
    FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER
  ),
  [FilterKey.SAMPLE_CONTAINER]: standardOperatorsForKey(FilterKey.SAMPLE_CONTAINER),
  [FilterKey.SAMPLE_DEFINITION]: standardOperatorsForKey(FilterKey.SAMPLE_DEFINITION),
  [FilterKey.SAMPLE]: standardOperatorsForKey(FilterKey.SAMPLE),
  [FilterKey.WORKSHEET]: standardOperatorsForKey(FilterKey.SAMPLE),
  [FilterKey.REQUEST_V2_SUBMISSION_STATUS]: standardOperatorsForKey(FilterKey.REQUEST_V2_SUBMISSION_STATUS),
  [FilterKey.REQUEST_V2_DEFINITION]: standardOperatorsForKey(FilterKey.REQUEST_V2_DEFINITION),
  [FilterKey.IS_FILL_IN_ENTRY]: standardOperatorsForKey(FilterKey.IS_FILL_IN_ENTRY),
  [FilterKey.PUBLISHING_STATUS]: standardOperatorsForKey(FilterKey.PUBLISHING_STATUS),
  [FilterKey.AA_PART_SEQUENCE]: [Operator.HAS_ONE_OF],
};

export const SINGLE_VALUE_FILTER_KEYS = [
  FilterKey.ACCOUNT_STATUS,
  FilterKey.ANCESTOR_FOLDER,
  FilterKey.ANCESTOR_STORABLE,
  FilterKey.ARCHIVE_STATUS,
  FilterKey.ASSAY_SCHEMA,
  FilterKey.CONTAINER_CONTENT_SCHEMA,
  FilterKey.CONTAINER_PARENT,
  FilterKey.FOLDER,
  FilterKey.FOLDER_SECTION,
  FilterKey.HIDDEN,
  FilterKey.INSTITUTION,
  FilterKey.ITEM_IN_WORKLIST,
  FilterKey.ORGANIZATION,
  FilterKey.PARENT_STORABLE,
  FilterKey.PROCESS_CLIENT_ORIGIN,
  FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS,
  FilterKey.PROJECT,
  FilterKey.PROJECT_ATTRIBUTE,
  FilterKey.PROJECT_FOLDER,
  FilterKey.PROTEIN_PROSITE,
  FilterKey.QUERY_API_PROJECT,
  FilterKey.QUERY_API_REGISTRY,
  FilterKey.REGISTRATION_STATUS,
  FilterKey.REGISTRY,
  FilterKey.REGISTRY_COLLECTION_OWNER,
  FilterKey.REQUEST_TASK_SCHEMA,
  FilterKey.ROOT_PROCESS_TASK_SCHEMA,
  FilterKey.RUN_AVAILABILITY,
  FilterKey.SESSION_NAME,
  FilterKey.SHARED_WITH,
  FilterKey.STARRED,
  FilterKey.STUDY_PHASE,
  FilterKey.TEAM,
  FilterKey.TEMPLATE_COLLECTION,
  FilterKey.TOP_LEVEL_PERMISSIONS,
  FilterKey.TYPE,
  FilterKey.WORKFLOW_STATUS,
  FilterKey.WORKFLOW_TEMPLATE,
] as const;
type SingleValueFilterKey = (typeof SINGLE_VALUE_FILTER_KEYS)[number];

export const MULTI_VALUE_FILTER_KEYS = [
  FilterKey.AA_PART_SEQUENCE,
  FilterKey.ANY_LINK_SCHEMA_FIELD,
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.AUTHOR,
  FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS,
  FilterKey.ASSAY_TEMPLATE,
  FilterKey.CAPABILITIES,
  FilterKey.CONNECTION,
  FilterKey.CONNECTION_ADAPTER,
  FilterKey.CONTAINER_CHECK_OUT_STATUS,
  FilterKey.CREATION_ORIGIN_ENTRY,
  FilterKey.CREATOR,
  FilterKey.ENTRY_TEMPLATE,
  FilterKey.INGREDIENTS,
  FilterKey.INVENTORY_VALIDATION_STATUS,
  FilterKey.IN_STUDY,
  FilterKey.PART_SEQUENCE,
  FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS,
  FilterKey.RECIPE_TEMPLATE,
  FilterKey.POLICY,
  FilterKey.PROCEDURE_TASK_STATUS,
  FilterKey.PROCESS,
  FilterKey.PROCESS_EXECUTION_TYPE,
  FilterKey.PROCESS_STATUS,
  FilterKey.PROCESS_TASK,
  FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY,
  FilterKey.PROCESS_TASK_STATUS,
  FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES,
  FilterKey.QUERY_API_BIOENTITY_ID,
  FilterKey.QUERY_API_ARCHIVE_REASON,
  FilterKey.QUERY_API_AUTHOR,
  FilterKey.QUERY_API_CREATOR,
  FilterKey.SAMPLE_RESTRICTION_ACCESS,
  FilterKey.SAMPLE_RESTRICTION_STATUS,
  FilterKey.REQUEST_REQUESTOR,
  FilterKey.REQUEST_STATUS,
  FilterKey.REQUEST_V2_DEFINITION,
  FilterKey.REVIEW_LIFECYCLE_TYPE,
  FilterKey.RNA_PART_SEQUENCE,
  FilterKey.ROOT_PROCESS,
  FilterKey.SCHEMA,
  FilterKey.SCHEMA_INTERFACE,
  FilterKey.SCHEMA_VALIDATION_STATUS,
  FilterKey.SESSION_STATUS,
  FilterKey.TRANSCRIPTIONS,
  FilterKey.TYPES,
  FilterKey.WORKLIST_TYPE,
  FilterKey.WORKSHEET,
] as const;
type MultiValueFilterKey = (typeof MULTI_VALUE_FILTER_KEYS)[number];

export const DATETIME_FILTER_KEYS = [
  FilterKey.QUERY_API_MODIFIED_AT,
  FilterKey.QUERY_API_CREATED_AT,
  FilterKey.SESSION_MODIFIED_AT,
] as const;
type DatetimeFilterKey = (typeof DATETIME_FILTER_KEYS)[number];

// These filters are distinguished by having a single string value but supporting multiple operators
export const FULL_TEXT_FILTER_KEYS = [
  FilterKey.SEQUENCE_BASES,
  FilterKey.OLIGO_HELM_DNA,
  FilterKey.OLIGO_HELM_RNA,
  FilterKey.PROTEIN_AMINO_ACIDS,
] as const;
type FullTextFilterKey = (typeof FULL_TEXT_FILTER_KEYS)[number];

interface BaseFilter {
  operator: Operator;
  value: unknown;
  // If set, the user should not be able to remove the filter. To prevent edits to the filter it may
  // be necessary to edit the corresponding filter form to respect the `locked` prop.
  locked?: boolean;
}

// Keep track of which filters have support for the `locked` attribute. Their UI should disable editing the filter
// when `locked` is truthy.
export const FILTERS_HANDLING_LOCKED: Array<FilterKey> = [
  FilterKey.PART_SEQUENCE,
  FilterKey.RNA_PART_SEQUENCE,
  FilterKey.TRANSCRIPTIONS,
];

export interface StandardFilter extends BaseFilter {
  key: StandardFilterKey;
  value: StandardFilterValue;
}

export interface SingleValueFilter extends BaseFilter {
  key: SingleValueFilterKey;
  value: string | null;
}

export interface SingleValueFilterWithDefault extends SingleValueFilter {
  key: SingleValueFilterKey;
  value: string;
}

export interface MultiValueFilter extends BaseFilter {
  key: MultiValueFilterKey;
  value: Array<string>;
}

export interface FullTextFilter extends BaseFilter {
  key: FullTextFilterKey;
  value: string;
}

export interface DatetimeFilter extends BaseFilter {
  key: DatetimeFilterKey;
  value: {
    fromDatetime: string | null;
    toDatetime: string | null;
  };
}

export interface GroupByKeyFilter extends BaseFilter {
  key: FilterKey.GROUP_BY_KEY;
  groupByField: string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  value: Array<any>;
}

export interface CustomTagFilter extends BaseFilter {
  key: FilterKey.CUSTOM_TAG;
  name: string | null;
  value: string | null;
}

const STRING_FIELD_TYPES = [FieldType.TEXT, FieldType.LONG_TEXT, FieldType.DATE, FieldType.DATETIME] as const;
type StringFieldType = (typeof STRING_FIELD_TYPES)[number];

export function isStringFieldType(fieldType: FieldType): fieldType is StringFieldType {
  return STRING_FIELD_TYPES.includes(fieldType as StringFieldType);
}

const NUMERIC_FIELD_TYPES = [FieldType.FLOAT, FieldType.INTEGER] as const;
type NumericFieldType = (typeof NUMERIC_FIELD_TYPES)[number];

export function isNumericFieldType(fieldType: FieldType): fieldType is NumericFieldType {
  return NUMERIC_FIELD_TYPES.includes(fieldType as NumericFieldType);
}

const MULTI_VALUE_FIELD_TYPES = [
  FieldType.SELECTOR,
  FieldType.BIOENTITY_LINK,
  FieldType.BLOB_LINK,
  FieldType.PART_LINK,
  FieldType.BATCH_LINK,
  FieldType.ENTRY_LINK,
  FieldType.TRANSCRIPTION_LINK,
  FieldType.TRANSLATION_LINK,
  FieldType.STORABLE_LINK,
  FieldType.AA_PART_LINK,
] as const;
export type MultiValueFieldType = (typeof MULTI_VALUE_FIELD_TYPES)[number];

export function isMultiValueFieldType(fieldType: FieldType): fieldType is MultiValueFieldType {
  return MULTI_VALUE_FIELD_TYPES.includes(fieldType as MultiValueFieldType);
}

interface BaseFieldFilter extends StandardFilter {
  key: FilterKey.SCHEMA_FIELD | FilterKey.BATCH_TAG;
  operator: Operator;
  fieldId: string | null;
}

export interface MultiLinkFieldFilter extends BaseFieldFilter {
  fieldType: MultiValueFieldType;
  value: Array<string> | null;
}

export interface NumericFieldFilter extends BaseFieldFilter {
  fieldType: NumericFieldType;
  value: StandardRangeOperatorValue | number | null;
}

export interface StringFieldFilter extends BaseFieldFilter {
  fieldType: StringFieldType;
  value: StandardRelativeRangeOperatorValue | StandardRangeOperatorValue | string | Array<string> | null;
}

export interface BooleanFieldFilter extends BaseFieldFilter {
  fieldType: FieldType.BOOLEAN;
}

export interface ReviewFilter extends BaseFilter {
  key: FilterKey.REVIEW;
  value: {
    review_status: string | null;
    reviewerStatus?: ReviewerStatus | null;
    role: string | null;
    terminalState?: ReviewLifecycleTerminalState | null;
  };
}

// renamed with the old prefix to help with backward compat typing
export interface OldOwnerFilter extends BaseFilter {
  key: FilterKey.OWNER;
  value: {
    userIds: Array<string>;
    organizationIds: Array<string>;
  };
}

export interface OwnerOrCollaboratorFilter extends BaseFilter {
  key: FilterKey.OWNER_OR_COLLABORATOR;
  value: {
    userIds: Array<string>;
    organizationIds: Array<string>;
    teamIds: Array<string>;
  };
}

export interface InstitutionFilter extends BaseFilter {
  key: FilterKey.INSTITUTION;
  value: {
    id: string;
    name: string;
  } | null;
}

export enum MentionableType {
  BATCH = 'Batch',
  BIOENTITY = 'Entity',
  ENTRY = 'Entry',
  STORAGE = 'Inventory',
  WORKLIST = 'Worklist',
}

export interface NoteReferenceFilter extends BaseFilter {
  key: FilterKey.NOTE_REFERENCE;
  value: {
    referencedType: MentionableType;
    referencedIds: Array<string>;
  };
}

export interface ContainerQuantityFilter extends BaseFilter {
  key: FilterKey.CONTAINER_QUANTITY;
  value: LegacyMeasurement;
}

export interface ContainerConcentrationFilter extends BaseFilter {
  key: FilterKey.CONTAINER_CONCENTRATION;
  value: LegacyMeasurement;
}

export interface GridEmptyPositionsFilter extends BaseFilter {
  key: FilterKey.GRID_EMPTY_POSITIONS;
  value: string | null;
}

export interface ContainerContentsFilter extends BaseFilter {
  key: FilterKey.CONTAINER_CONTENTS;
  value: {
    contentIds: Array<string>;
  };
}

export interface OligoHELMFilterDNA extends BaseFilter {
  key: FilterKey.OLIGO_HELM_DNA;
  value: string;
}

export interface OligoHELMFilterRNA extends BaseFilter {
  key: FilterKey.OLIGO_HELM_RNA;
  value: string;
}

export enum MonomerFilterQualifier {
  AT_POS = 'AT_POS',
  ANY_POS = 'ANY_POS',
  MIN_COUNT = 'MIN_COUNT',
  MAX_COUNT = 'MAX_COUNT',
}

export interface MonomerFilter extends BaseFilter {
  key: FilterKey.MONOMER;
  value: string | MissingMonomerReason;
  qualifier: MonomerFilterQualifier;
  qualifierValue?: number | Array<number>;
}

export enum ChemicalStructureFilterFormat {
  MOL = 'MOL',
  SMILES = 'SMILES',
}

export interface ChemicalStructureFilter extends BaseFilter {
  key: FilterKey.CHEMICAL_STRUCTURE;
  format: ChemicalStructureFilterFormat;
  value: string;
  minSimilarity?: number;
}

export interface BLASTTextSequence {
  sequence: string;
  name: string;
}

export enum BLASTStrand {
  BOTH = 'both',
  PLUS = 'plus',
  MINUS = 'minus',
}

export interface BLASTGapCost {
  existence: number;
  extension?: number;
}

export interface BLASTMatchScore {
  reward: number;
  penalty: number;
}

export enum BLASTMatrix {
  PAM30 = 'PAM30',
  PAM70 = 'PAM70',
  PAM250 = 'PAM250',
  BLOSUM45 = 'BLOSUM45',
  BLOSUM50 = 'BLOSUM50',
  BLOSUM62 = 'BLOSUM62',
  BLOSUM80 = 'BLOSUM80',
  BLOSUM90 = 'BLOSUM90',
}

export enum BLASTCompAdjustment {
  NoAdjustment = '0',
  CompBasedStats = '1',
  ConditionalAdjustment = '2',
  UnconditionalAdjustment = '3',
}

export const BLASTCompAdjustmentLabels: Record<BLASTCompAdjustment, string> = {
  [BLASTCompAdjustment.NoAdjustment]: 'No adjustment',
  [BLASTCompAdjustment.CompBasedStats]: 'Composition-based statistics',
  [BLASTCompAdjustment.ConditionalAdjustment]: 'Conditional compositional score matrix adjustment',
  [BLASTCompAdjustment.UnconditionalAdjustment]: 'Unconditional compositional score matrix adjustment',
};

export type BLASTOperator =
  | Operator.BLASTN
  | Operator.BLASTP
  | Operator.BLASTX
  | Operator.TBLASTN
  | Operator.TBLASTX;

/**
 * This interface uses snake case to match the field names in the backend
 * defined in services/search-blast/client/openapi.yaml.
 */
export interface AllBLASTParams<BLASTType> {
  task?: BLASTType;
  evalue?: number;
  culling_limit?: number;
  word_size?: number;
  gapopen?: number;
  gapextend?: number;
  reward?: number;
  penalty?: number;
  strand?: BLASTStrand;
  matrix?: BLASTMatrix;
  comp_based_stats?: BLASTCompAdjustment;
}

export enum BLASTNTask {
  MegaBLAST = 'megablast',
  DCMegaBLAST = 'dc-megablast',
  BLASTN = 'blastn',
  BLASTNShort = 'blastn-short',
}

export type BLASTNParams = Pick<
  AllBLASTParams<BLASTNTask>,
  | 'task'
  | 'evalue'
  | 'word_size'
  | 'culling_limit'
  | 'gapopen'
  | 'gapextend'
  | 'reward'
  | 'penalty'
  | 'strand'
>;

export enum BLASTPTask {
  BLASTP = 'blastp',
  BLASTPShort = 'blastp-short',
  BLASTPFast = 'blastp-fast',
}

export type BLASTPParams = Pick<
  AllBLASTParams<BLASTPTask>,
  'task' | 'evalue' | 'word_size' | 'culling_limit' | 'gapopen' | 'gapextend' | 'matrix' | 'comp_based_stats'
>;

export enum BLASTXTask {
  BLASTX = 'blastx',
  BLASTXFast = 'blastx-fast',
}

export type BLASTXParams = Pick<
  AllBLASTParams<BLASTXTask>,
  | 'task'
  | 'evalue'
  | 'word_size'
  | 'culling_limit'
  | 'gapopen'
  | 'gapextend'
  | 'strand'
  | 'matrix'
  | 'comp_based_stats'
>;

export enum TBLASTNTask {
  TBLASTN = 'tblastn',
  TBLASTNFast = 'tblastn-fast',
}

export type TBLASTNParams = Pick<
  AllBLASTParams<TBLASTNTask>,
  'task' | 'evalue' | 'word_size' | 'culling_limit' | 'gapopen' | 'gapextend' | 'matrix' | 'comp_based_stats'
>;

export type TBLASTXParams = Pick<
  AllBLASTParams<never>,
  'evalue' | 'word_size' | 'culling_limit' | 'strand' | 'matrix'
>;

export interface BLASTTaskTypeMap {
  [Operator.BLASTN]: BLASTNTask;
  [Operator.BLASTP]: BLASTPTask;
  [Operator.BLASTX]: BLASTXTask;
  [Operator.TBLASTN]: TBLASTNTask;
  [Operator.TBLASTX]: never;
}

export interface BLASTParamsTypeMap {
  [Operator.BLASTN]: BLASTNParams;
  [Operator.BLASTP]: BLASTPParams;
  [Operator.BLASTX]: BLASTXParams;
  [Operator.TBLASTN]: TBLASTNParams;
  [Operator.TBLASTX]: TBLASTXParams;
}

export type BLASTParamsUnion = BLASTParamsTypeMap[keyof BLASTParamsTypeMap];

export const hasTaskParam = (
  params: BLASTParamsUnion
): params is BLASTNParams | BLASTPParams | BLASTXParams | TBLASTNParams =>
  (params as BLASTNParams).task != null;

export const hasMatrixParam = (
  params: BLASTParamsUnion
): params is BLASTPParams | BLASTXParams | TBLASTNParams | TBLASTXParams =>
  (params as BLASTPParams).matrix != null;

export const hasStrandParam = (
  params: BLASTParamsUnion
): params is BLASTNParams | BLASTXParams | TBLASTXParams => (params as BLASTNParams).strand != null;

export const hasMatchScoreParam = (params: BLASTParamsUnion): params is BLASTNParams => {
  const typedParams = params as BLASTNParams;
  return typedParams.reward != null && typedParams.penalty != null;
};

export interface BLASTFilter extends BaseFilter {
  key: FilterKey.BLAST;
  value: {
    textSequences: Array<BLASTTextSequence>;
    apiIdentifiers: Array<string>;
  };
  params?: BLASTParamsUnion;
}

export interface PartSequenceFilter extends BaseFilter {
  key: FilterKey.PART_SEQUENCE;
  value: Array<string>;
}

export interface RnaPartSequenceFilter extends BaseFilter {
  key: FilterKey.RNA_PART_SEQUENCE;
  value: Array<string>;
}

export interface AaPartSequenceFilter extends BaseFilter {
  key: FilterKey.AA_PART_SEQUENCE;
  value: Array<string>;
}

export interface ProteinStructureFilter extends BaseFilter {
  key: FilterKey.ALPHAFOLD_STRUCTURE;
  value: null; // Just rely on the operator; value not applicable.
}

export interface TranscriptionFilter extends BaseFilter {
  key: FilterKey.TRANSCRIPTIONS;
  value: Array<string>;
}

export type FieldFilter = MultiLinkFieldFilter | NumericFieldFilter | StringFieldFilter | BooleanFieldFilter;
// This will error if any members of FieldFilter do not have a `fieldType` field.
export type _AllFieldFiltersHaveFieldTypesTest = FieldFilter['fieldType'];

export type Filter =
  | SingleValueFilter
  | MultiValueFilter
  | DatetimeFilter
  | StandardFilter
  | ChemicalStructureFilter
  | CustomTagFilter
  | FieldFilter
  | GroupByKeyFilter
  | NoteReferenceFilter
  | OwnerOrCollaboratorFilter
  | ReviewFilter
  | ContainerQuantityFilter
  | ContainerConcentrationFilter
  | GridEmptyPositionsFilter
  | ContainerContentsFilter
  | OligoHELMFilterDNA
  | OligoHELMFilterRNA
  | InstitutionFilter
  | FullTextFilter
  | MonomerFilter
  | BLASTFilter
  | ProteinStructureFilter;

/**
 * Helper type to get the `Filter` union member associated with the input `FilterKey` `T`.
 *
 * If `T` has multiple corresponding `Filter` types, this will return the union of all of them.
 *
 * @example
 * const dateTimeFilter: GenericFilter<FilterKey.MODIFIED_AT> = {...}; // type: DateTimeFilter
 */
// Extract<Filter, {key: T}> does not work because some Filter-type key fields are unions of various
// FilterKeys. For example, DatetimeFilter['key'] is a union of CREATED_AT, MODIFIED_AT, etc.
// This code handles that case.
export type GenericFilter<T extends FilterKey = FilterKey> = Exclude<Filter, {key: Exclude<FilterKey, T>}>;

// This will type-error if there is a `Filter` member that does not have a `key` field.
export type _AllFiltersHaveKeysTest = Filter['key'];

export const isSingleValueFilterKey = (key: FilterKey): key is SingleValueFilterKey =>
  SINGLE_VALUE_FILTER_KEYS.includes(key as SingleValueFilterKey);

export const isSingleValueFilter = (filter: Filter): filter is SingleValueFilter =>
  isSingleValueFilterKey(filter.key);

export const isMultiValueFilterKey = (key: FilterKey): key is MultiValueFilterKey =>
  MULTI_VALUE_FILTER_KEYS.includes(key as MultiValueFilterKey);

export const isMultiValueFilter = (filter: Filter): filter is MultiValueFilter =>
  isMultiValueFilterKey(filter.key);

export const isStandardFilterKey = (key: FilterKey): key is StandardFilterKey =>
  STANDARD_FILTER_KEYS.includes(key as StandardFilterKey);

export const isStandardFilter = (filter: Filter): filter is StandardFilter => isStandardFilterKey(filter.key);

export const isStandardStringFilter = (filter: Filter): filter is StandardFilter =>
  isStandardFilter(filter) && FILTERS[filter.key].dataType === StandardFilterDataType.STRING;

export const isStandardDatetimeFilter = (filter: Filter): filter is StandardFilter =>
  isStandardFilter(filter) && FILTERS[filter.key].dataType === StandardFilterDataType.DATETIME;

export const isStandardDateFilter = (filter: Filter): filter is StandardFilter =>
  isStandardFilter(filter) && FILTERS[filter.key].dataType === StandardFilterDataType.DATE;

export const isInStudyFilter = (filter: Filter): filter is MultiValueFilter =>
  filter.key === FilterKey.IN_STUDY;

export function isDatetimeFilterKey(key: FilterKey): key is DatetimeFilterKey {
  return DATETIME_FILTER_KEYS.includes(key as DatetimeFilterKey);
}

export const isDatetimeFilter = (filter: Filter): filter is DatetimeFilter => isDatetimeFilterKey(filter.key);

export const isFieldFilterKey = (key: FilterKey): key is FilterKey.BATCH_TAG | FilterKey.SCHEMA_FIELD =>
  [FilterKey.BATCH_TAG, FilterKey.SCHEMA_FIELD].includes(key);

export const isCustomTagFilter = (filter: Filter): filter is CustomTagFilter =>
  filter.key === FilterKey.CUSTOM_TAG;

export const isFieldFilter = (filter: Filter): filter is FieldFilter => isFieldFilterKey(filter.key);

export const isReviewFilter = (filter: Filter): filter is ReviewFilter => filter.key === FilterKey.REVIEW;

export function isContainerQuantityFilter(filter: Filter): filter is ContainerQuantityFilter {
  return filter.key === FilterKey.CONTAINER_QUANTITY;
}

export function isContainerConcentrationFilter(filter: Filter): filter is ContainerConcentrationFilter {
  return filter.key === FilterKey.CONTAINER_CONCENTRATION;
}

export function isFullTextFilterKey(key: FilterKey): key is FullTextFilterKey {
  return FULL_TEXT_FILTER_KEYS.includes(key as FullTextFilterKey);
}

export function isFullTextFilter(filter: Filter): filter is FullTextFilter {
  return isFullTextFilterKey(filter.key);
}

export function isMonomerFilter(filter: Filter): filter is MonomerFilter {
  return filter.key === FilterKey.MONOMER;
}

export function isChemicalStructureFilter(filter: Filter): filter is ChemicalStructureFilter {
  return filter.key === FilterKey.CHEMICAL_STRUCTURE;
}

export function isBLASTFilter(filter: Filter): filter is BLASTFilter {
  return filter.key === FilterKey.BLAST;
}

export const isStringFieldFilter = (filter: FieldFilter): filter is StringFieldFilter =>
  STRING_FIELD_TYPES.includes(filter.fieldType as StringFieldType);

export const isMultiLinkFieldFilter = (filter: FieldFilter): filter is MultiLinkFieldFilter =>
  MULTI_VALUE_FIELD_TYPES.includes(filter.fieldType as MultiValueFieldType);

export const isNumericFieldFilter = (filter: FieldFilter): filter is NumericFieldFilter =>
  NUMERIC_FIELD_TYPES.includes(filter.fieldType as NumericFieldType);

function _keyToEmptyFilter(key: FilterKey): Filter {
  if (isStandardFilterKey(key)) {
    // These filters are always defaulted to false
    if (
      key === FilterKey.IS_ASSOCIATED_WITH_UNSUBMITTED_REQUEST_V2_SUBMISSION ||
      key === FilterKey.PROCESSES_IS_SYSTEM_DATA_FILTER
    ) {
      return {
        key,
        operator: Operator.IS_FALSE,
        value: null,
      };
    }
    const dataType = FILTERS[key].dataType;
    if (!dataType) {
      throw new Error(
        `Attempted to get default operator for standard filter ${key}, but dataType was not specified for key in FILTERS.`
      );
    }
    const operator = FILTER_KEY_TO_OPERATORS[key][0];
    const defaultValueFactory = STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY[operator];
    if (!defaultValueFactory) {
      throw new Error(
        `Attempted to create default value for standard filter operator ${operator}, but no default value factory exists in STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY`
      );
    }
    return {
      key,
      operator,
      value: defaultValueFactory(),
      ...(isFieldFilterKey(key) ? {fieldId: null, fieldType: FieldType.TEXT} : {}),
    } as Filter; // TODO: Remove cast and fix the types of key and value. Introduced by Typescript 5.7.3 upgrade.
  }
  const operator = FILTER_KEY_TO_OPERATORS[key][0];
  // The STARRED FilterKey is a SingleValueFilter. It needs to
  // come before the isSingleValueFilterKey check otherwise
  // it will get filtered out.
  if (key === FilterKey.STARRED) {
    return {
      key,
      operator,
      value: 'starred',
    };
  } else if (key === FilterKey.ARCHIVE_PURPOSES) {
    return {
      key,
      operator,
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    };
  } else if (key === FilterKey.MONOMER) {
    return {
      key,
      operator,
      value: MissingMonomerReason.CHOOSING,
      qualifier: MonomerFilterQualifier.ANY_POS,
    };
  } else if (key === FilterKey.GROUP_BY_KEY) {
    return {
      key,
      operator: Operator.IS_ONE_OF,
      groupByField: '',
      value: [],
    };
  } else if (key === FilterKey.CHEMICAL_STRUCTURE) {
    return {
      key,
      operator,
      format: ChemicalStructureFilterFormat.SMILES,
      value: '',
    };
  } else if (key === FilterKey.BLAST) {
    return {
      key,
      operator,
      value: {textSequences: [], apiIdentifiers: []},
    };
  } else if (key === FilterKey.ALPHAFOLD_STRUCTURE) {
    return {
      key,
      operator,
      value: null,
    };
  } else if (isFullTextFilterKey(key)) {
    return {
      key,
      operator,
      value: '',
    };
  } else if (isSingleValueFilterKey(key)) {
    return {
      key,
      operator,
      value: null,
    };
  } else if (isMultiValueFilterKey(key)) {
    return {
      key,
      operator,
      value: [],
    };
  } else if (isDatetimeFilterKey(key)) {
    return {
      key,
      operator,
      value: {
        fromDatetime: null,
        toDatetime: null,
      },
    };
  } else if (key === FilterKey.CUSTOM_TAG) {
    return {
      key,
      operator,
      name: null,
      value: null,
    };
  } else if (key === FilterKey.REVIEW) {
    return {
      key,
      operator,
      value: {
        review_status: null,
        role: null,
      },
    };
  } else if (key === FilterKey.CONTAINER_QUANTITY) {
    return {
      key,
      operator,
      value: {
        units: null,
        value: null,
      },
    };
  } else if (key === FilterKey.CONTAINER_CONCENTRATION) {
    return {
      key,
      operator,
      value: {
        units: null,
        value: null,
      },
    };
  } else if (key === FilterKey.GRID_EMPTY_POSITIONS) {
    return {
      key,
      operator,
      value: null,
    };
  } else if (key === FilterKey.CONTAINER_CONTENTS) {
    return {
      key,
      operator,
      value: {
        contentIds: [],
      },
    };
  } else if (key === FilterKey.NOTE_REFERENCE) {
    return {
      key,
      operator,
      value: {
        referencedType: MentionableType.BIOENTITY,
        referencedIds: [],
      },
    };
  }
  // @ts-expect-error TODO(BNCH-56923): Currently `FilterKey.TAG` is not handled.
  // Also update `client/worklists/barcode-scanning/__tests__/ScanIntoWorklistModal-test.tsx` (look for TODO)
  // And `client/worklists/barcode-scanning/useWorklistItemsFromBarcodeScanning.ts`
  throw new Error(`Unexpected filter key ${assertNever(key)}`);
}

export function keyToEmptyFilter<T extends FilterKey = FilterKey>(filterKey: T): GenericFilter<T> {
  return _keyToEmptyFilter(filterKey) as GenericFilter<T>;
}

export enum SearchType {
  ANALYSES_AND_ANALYTICS_DASHBOARDS = 'ANALYSES_AND_ANALYTICS_DASHBOARDS',
  BENCHLING_APPS = 'BENCHLING_APPS',
  BIOENTITIES = 'BIOENTITIES',
  CONNECTIONS = 'CONNECTIONS',
  FOLDERS_AND_ITEMS = 'FOLDERS_AND_ITEMS',
  GLOBAL = 'GLOBAL',
  JELLYFISH = 'JELLYFISH',
  PROCESS_MANAGEMENT = 'PROCESS_MANAGEMENT',
  PROJECTS = 'PROJECTS',
  REQUEST = 'REQUEST',
  REQUEST_V2_DASHBOARD = 'REQUEST_V2_DASHBOARD',
  REQUEST_V2_TYPES = 'REQUEST_V2_TYPES',
  RUN = 'RUN',
  SAMPLE = 'SAMPLE',
  STORABLE = 'STORABLE',
  STUDY_ITEMS = 'STUDY_ITEMS',
  STUDIES = 'STUDIES',
  TEMPLATE_COLLECTION = 'TEMPLATE_COLLECTION',
  WORKFLOW = 'WORKFLOW',
  WORKLIST = 'WORKLIST',
  WORKLIST_ITEMS = 'WORKLIST_ITEMS',
  WORKSHEET = 'WORKSHEET',
}

export const GLOBAL_SEARCH_URL = '/1/api/search';
export const DROPDOWN_SEARCH_URL = '/1/api/dropdown/search';

export enum DropdownSearchType {
  AT_MENTION = 'AT_MENTION',
  ENTITY_LINK = 'ENTITY_LINK',
}

export enum SortKey {
  SORT_BARCODE_KEY = 'barcode',
  SORT_BLAST_E_VALUE = 'blast_e_value',
  SORT_REGISTRY_IDENTIFIER_KEY = 'collection_alias',
  SORT_CREATED_AT_KEY = 'created_at',
  SORT_NAME_KEY = 'name',
  SORT_IDENTIFIER_KEY = 'identifier',
  SORT_MODIFIED_AT_KEY = 'modified_at',
  SORT_WORKLIST_POSITION_KEY = 'worklist_position',
  SORT_RELEVANCE_KEY = 'relevance',
  SORT_SCHEDULED_ON_KEY = 'scheduled_on',
  SORT_SCHEMA_NAME = 'schema_name',
  SORT_CREATOR_NAME = 'creator_name',
  SORT_EXECUTION_TYPE = 'execution_type',
  SORT_EXPIRATION_DATE = 'expiration_date',
  SORT_FREEZE_THAW_COUNT = 'freeze_thaw_count',
  SORT_RECIPE_TEMPLATE_NAME = 'procedure_template_name',
  SORT_ASSAY_TEMPLATE_NAME = 'assay_template_name',
  SORT_PROCESS_ID = 'workflow_task_group_id',
  SORT_PROCESS_TASK_TYPE = 'workflow_task_type',
  SORT_ASSIGNEE_NAME = 'assignee_name',
  SORT_STATUS = 'status',
  SORT_PROCESS_TASK_ID = 'workflow_task_id',
  SORT_CHEMICAL_SIMILARITY = 'chemical_similarity',
  SORT_SAMPLE_RESTRICTION_STATUS = 'sample_restriction_status',
  SORT_STARRED = 'starred',
  SORT_CONDITION_NUMBERS = 'procedure_run_condition_numbers',
  SORT_REPLICATE_NUMBERS = 'procedure_run_replicate_numbers',
  SORT_PROCEDURE_METHOD_FLOWCHART_ORDER = 'procedure_method_flowchart_order',
  SORT_RECIPE_METHOD_RECIPE_ORDER = 'procedure_method_recipe_order',
  SORT_ASSAY_METHOD_ASSAY_ORDER = 'assay_method_assay_order',
  SORT_RECIPE_STEP_RECIPE_ORDER = 'procedure_step_recipe_order',
  SORT_ASSAY_STEP_ASSAY_ORDER = 'assay_step_assay_order',
  SORT_REQUEST_V2_SUBMISSION_STATUS = 'request_v2_submission_status',
  SORT_REQUEST_V2_DEFINITION_NAME = 'request_v2_definition_name',
}

export enum MultiSortKey {
  SORT_RECIPE_ORDER = 'recipe_order',
}

// A next token is treated as an opaque token on the client, but we need to ensure it is JSON serializable
// so we make it a JSONValue. The type alias is just for expresiveness, and to ease migration to an explicit
// interface if we need to access its contents in the future.
export type SearchNextToken = JSONValue;

export interface SearchParams {
  offset: number;
  limit: number;
  query: string;
  filters: Array<Filter>;
  sorts: Array<Sort>;
  groupBy?: GroupByParams | null;
  // The "null" union isn't strictly required while SearchNextToken is just an alias for
  // unknown, but we include it explicitly for the expressive power of signaling that a
  // nextToken is optional (such as when retrieving the first page, or paging purely by
  // offset).
  nextToken?: SearchNextToken | null;
}

export interface Sort {
  key: string;
  reverse: boolean;
}

export interface GroupByParams {
  groupField: string;
  childOffset: number;
  childLimit: number;
}

export type FilterErrors = Array<string | null>;

export interface ScoresById {
  [id: string]: number | null;
}

export interface GroupAggregates {
  [key: string]: number | string | LegacyMeasurement | null;
}

export interface ItemResult {
  id: string;
  score: number | null;
  attributeValues: {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  // TODO(BNCH-145194): remove this custom context when removing reaction search prototype
  reactionContext?: ReactionContext;
}

export interface MatchedMolecule {
  moleculeId: string;
  role: ChemistryReactionRole.REACTANT | ChemistryReactionRole.PRODUCT;
}

export interface ReactionContext {
  tableUuid: string;
  marvinContent: string;
  matchedMolecules: Array<MatchedMolecule>;
}

export interface GroupResult {
  key: string;
  label: string;
  count: number;
  childIds: Array<string>;
  childItems: Array<ItemResult>;
  aggregates: GroupAggregates;
}

export enum SearchStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
}

export interface AsyncSearchResponse {
  searchId: string;
  status: SearchStatus.IN_PROGRESS | SearchStatus.FAILED;
  errorMessage?: string;
}

export interface SearchResults {
  searchId: string;
  status: SearchStatus.COMPLETE;
  ids: Array<string>;
  groupItemIds: Array<string>;
  // This would ideally just be named "items", but several DAOs extend this with their own "items" attribute
  // that cause a name clash. Consider renaming later once we consolidate listing endpoints.
  itemResults?: Array<ItemResult>;
  total: number;
  isExact: boolean;
  timedOut: boolean;
  correlationId?: string;
  scoresById?: ScoresById;
  groups?: Array<GroupResult>;
  nextToken?: SearchNextToken | null;
  attributes?: Array<string>;
}

export type SearchResponse = AsyncSearchResponse | SearchResults;

export function isCompleteSearchResponse(response: SearchResponse): response is SearchResults {
  return response.status === SearchStatus.COMPLETE;
}

export interface DropdownSearchResults {
  ids: Array<string>;
  total: number;
  isExact: boolean;
  timedOut: boolean;
  correlationId: string;
  groups?: Array<GroupResult>;
  attributes?: Array<string>;
}

export interface UIListingState {
  viewSelection: SearchView;
  groupBySelection: string | undefined; // id of the selected ListableGroup
  multiqueryBlastOffset: {[groupId: string]: number};
  asyncFetchFlag: boolean;
}

export const DEFAULT_LISTING_STATE = {
  viewSelection: SearchView.TABLE,
  groupBySelection: undefined,
  multiqueryBlastOffset: {},
  asyncFetchFlag: true,
};

export enum AsyncFetcherLocation {
  ITEM_LISTING_ENHANCE_PROPS = 'itemListingEnhanceProps',
  EXPANDED_ITEM_TABLE_GQL = 'expandedItemTableGQL',
  EXPANDED_ITEM_TABLE_ENHANCE_PROPS = 'expandedItemTableEnhanceProps',
  SIDEPANEL_ITEM_LIST = 'sidepanelItemList',
}

export interface AsyncFetcherIsFetchedState {
  itemListingEnhanceProps: boolean;
  expandedItemTableGQL: boolean;
  expandedItemTableEnhanceProps: boolean;
  sidepanelItemList: boolean;
}

export const DEFAULT_ASYNC_FETCHER_IS_FETCHED_STATE = {
  itemListingEnhanceProps: false,
  expandedItemTableGQL: false,
  expandedItemTableEnhanceProps: false,
  sidepanelItemList: false,
};

export const DEPRECATED_FILTER_PREFIXES = new Set(['contentAttribute:']);
export const DEPRECATED_FILTER_KEYS = new Set(['CONTAINER_CONTENT_SCHEMA_FIELD']);

export const FILTER_SEPARATOR = ';';
export const FILTER_PART_SEPARATOR = ':';
export const FILTER_VALUE_SEPARATOR = ',';

export const FILTER_SHAPE = PropTypes.shape({
  key: PropTypes.string.isRequired,
  operator: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
});

export const SEARCH_FILTER_SHAPE = PropTypes.shape({
  filter: FILTER_SHAPE,
  id: PropTypes.string.isRequired,
});

const TEXT_FIELD_TYPES = [FieldType.TEXT, FieldType.LONG_TEXT] as const;
type TextFieldType = (typeof TEXT_FIELD_TYPES)[number];

export function isTextFieldType(fieldType: FieldType): fieldType is TextFieldType {
  return TEXT_FIELD_TYPES.includes(fieldType as TextFieldType);
}

export const COMPARABLE_FIELD_TYPES = [...NUMERIC_FIELD_TYPES, FieldType.DATE, FieldType.DATETIME];

export type SchemaFilters = RegistryFilters | BatchSchemaFilters;

export const MIN_QUERY_LENGTH = 3;
export const SHORT_QUERY_MESSAGE = `Searches under ${MIN_QUERY_LENGTH} characters return limited results`;
export const LISTING_ERROR_MESSAGE =
  'Sorry, we ran into an error returning your results. Please contact support if this continues.';
export const NO_RESULTS_MESSAGE = 'No items found';
export const NO_REMAINING_SORTS_MESSAGE = 'Additional sort fields are not available';

// This is slightly out of place in a file of constants, but has cohesion with NO_RESULTS_MESSAGE so
// including it here despite the awkwardness.
export function getNoResultsMessage(
  typesFilter: MultiValueFilter | null,
  interfaceFilter?: MultiValueFilter | null
): string {
  if (typesFilter?.value.length !== 1) {
    return NO_RESULTS_MESSAGE;
  }
  if (interfaceFilter?.value.length === 1 && APIUtil.isStaticSchemaInterface(interfaceFilter.value[0])) {
    // Lazy require: a static import pulls TagSchemaItemTypeUtil (and dependents) while SearchConstants is
    // still initializing, which can cause circular-init / TDZ failures in the react-render bundle.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const {getItemTypeFromStaticSchemaInterfaceId, getItemTypeDisplayName} =
      require('schemas/TagSchemaItemTypeUtil') as typeof import('schemas/TagSchemaItemTypeUtil');
    const maybeSsiItemType = getItemTypeFromStaticSchemaInterfaceId(interfaceFilter.value[0]);
    if (maybeSsiItemType) {
      return `No ${getItemTypeDisplayName(maybeSsiItemType, true).toLowerCase()} found`;
    }
  }
  return `No ${typeToPluralized(typesFilter.value[0])} found`;
}

// TODO(BNCH-64980): Display a more specific message with total results when we consolidate dropdown components
// Currently, search dropdowns don't make it clear that there are more results than the ones shown, so we wanted to
// display this message when the SHORT_QUERY_MESSAGE isn't shown.
// If we know the limit from the search params, then we display it when the limit matches the number of results,
// otherwise when the number of results are more than the threshold.
export const REFINE_INPUT_THRESHOLD = 10;
export const REFINE_INPUT_MESSAGE = `Showing top results. Refine your input for more specific results`;

// Searches flow through a few generic endpoints but we sometimes only want results that are allowed for
// the action being taken. For example, we shouldn't return entities for a transfer that you don't have
// transfer access for. So, to let the search system know why the request is being made we provide a
// "context" that lets the search system filter results to those relevant to the action.
// Keep in sync with benchling/api/lib/search/constants.py
export enum SearchContext {
  LISTING = 'LISTING',
  FOR_WRITE_ACTION = 'FOR_WRITE_ACTION',
}

export const SORT_SEPARATOR = ';';
export const SORT_PART_SEPARATOR = ':';
export const SORT_REVERSE_TRUE_URL_STRING = '1';
export const SORT_REVERSE_FALSE_URL_STRING = '0';

export const SEARCH_CORRELATION_ID_HEADER = 'X-Search-Correlation-Id';

export const SCORE_SORTS = [SortKey.SORT_CHEMICAL_SIMILARITY];

export const CHEMICAL_STRUCTURE_SEARCH_OPERATORS = [Operator.SUBSTRUCTURE, Operator.SIMILARITY] as const;
export type ChemicalStructureSearchOperator = (typeof CHEMICAL_STRUCTURE_SEARCH_OPERATORS)[number];

export interface ChemicalStructureSearchParams {
  chemicalStructureQuery: ChemicalStructureDrawerValue;
  chemSearchOperator: ChemicalStructureSearchOperator;
  minSimilarity?: number;
}

export enum QuickFilterKey {
  TASKS_ASSIGNED_TO_CURRENT_USER = 'TASKS_ASSIGNED_TO_CURRENT_USER',
}

export interface QuickFilterConfiguration {
  title: string;
  quickFilterKey: QuickFilterKey;
  // We use a function to allow for filters which may need to be dynamic based on tenant specific settings or
  // config data. For example a filter that shows "Pending tasks" needs to be able to use the tenant's
  // pending status object ID.
  getFilters: () => Array<Filter>;
}

export const QUICK_FILTER_KEY_TO_CONFIGURATION: Record<QuickFilterKey, QuickFilterConfiguration> = {
  [QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER]: {
    title: 'Tasks assigned to me',
    quickFilterKey: QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER,
    getFilters: () => [
      {...keyToEmptyFilter(FilterKey.PROCESS_TASK_ASSIGNEE), value: null, operator: Operator.CURRENT_USER},
    ],
  },
};
